import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Upload, Search, Package, Coins, CalendarClock, BarChart3,
  Check, AlertTriangle, Download, Camera, Settings, Trash2, FileSpreadsheet, TrendingUp, LogIn, LogOut, Cloud, CloudOff, ExternalLink, History, Tag, ShoppingCart, SlidersHorizontal, Bot, Megaphone, ChevronDown,
} from "lucide-react";
import XLSXStyle from "xlsx-js-style";
import Chart from "chart.js/auto";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { app, loginWithGoogle, loginAnonymously, logout, watchAuth, saveData, loadData, watchData, saveHistoryDay, loadAllHistory, getReportConfig, saveReportConfig } from "./firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import PurchasePlanHistoryView from "./PurchasePlanHistoryView";
import SalesPurchaseView from "./SalesPurchaseView";
import NaverInsightView from "./NaverInsightView";
import WeeklyReportView from "./WeeklyReportView";
import PurchaseSimulationView from "./PurchaseSimulationView";
import PurchaseSimulation2View from "./PurchaseSimulation2View";
import PaymentDueView from "./PaymentDueView";
import { fetchProcurementPlans, plansToRawPlan } from "./procurementPlans";
import { fetchLatestPriceCost } from "./priceManager";
import { ACODE_MAP, PRODUCT_NAMES } from "./acodeMap";

const KEY = "pms4:data";
const CATEGORY_RULES=[
  ["위생랩","위생랩"],["지퍼백","지퍼백"],["롤백","롤백"],["니트릴","니트릴장갑"],
  ["위생장갑","위생장갑"],["수세미","수세미"],["행주","행주"],["키친타올","키친타올"],
  ["타올","타올"],["봉투","봉투"],["장갑","기타장갑"],
];
const categoryOf=(name)=>{const s=String(name||"");for(const [kw,label] of CATEGORY_RULES){if(s.includes(kw))return label;}return "기타";};
const toNum = (v) => { if (v==null||v==="") return 0; const n=Number(String(v).replace(/[^0-9.\-]/g,"")); return isNaN(n)?0:n; };
const won = (n) => n==null?"—":"₩"+Math.round(n).toLocaleString("ko-KR");
const nf = (n) => (Number(n)||0).toLocaleString("ko-KR");
const dc = (n,d=1) => n==null||!isFinite(n)?"—":Number(n).toFixed(d);
const norm = (s) => String(s??"").trim().replace(/\s+/g,"").replace(/\n/g,"");
const fmtDate = (v) => {
  if (v instanceof Date) return v.toISOString().slice(0,10);
  if (typeof v==="number"&&v>40000&&v<60000){const d=new Date(Math.round((v-25569)*86400*1000));return d.toISOString().slice(0,10);}
  const s=String(v).trim(); const m=s.match(/(\d{4})[-./](\d{1,2})[-./](\d{1,2})/);
  return m?`${m[1]}-${m[2].padStart(2,"0")}-${m[3].padStart(2,"0")}`:null;
};
// 파일명에서 날짜 추출: 2026-06-13 / 2026.06.13 / 20260613 형식 지원
const dateFromFilename = (name) => {
  const s=String(name);
  let m=s.match(/(20\d{2})[-_.]?(0[1-9]|1[0-2])[-_.]?(0[1-9]|[12]\d|3[01])/);
  if(m)return `${m[1]}-${m[2]}-${m[3]}`;
  return null;
};
function findHeaderRow(rows){for(let i=0;i<Math.min(rows.length,6);i++){const s=(rows[i]||[]).map(norm);if(s.includes("상품코드")||s.includes("품목코드"))return i;}return 0;}
function detectType(headers){
  if(headers.includes("정상재고"))return"stock";
  if(headers.includes("입고단가")&&headers.includes("품목코드"))return"cost";
  if(headers.includes("수량")&&(headers.includes("1일판매수량")||headers.includes("판매가능일수")||headers.includes("판매금액")))return"sales";
  if(headers.includes("발주량"))return"plan";
  return"unknown";
}
const TYPE_LABEL={stock:"현재고",sales:"월별 판매",cost:"단가 마스터",plan:"구매계획",salesDaily:"일별 판매"};
const CODE_ORDER=["12618","12619","12620","12903","12902","12901","12942","12943","12944","12945","12946","12947","12948","12769","12504","12623","12509","12510","12511","12711","12520","12519","12521","13638","12516","12517","12518","12712","12523","12522","12524","13643","12513","12514","12515","13639","13640","13641","13642","12753","12754","12755","12756","12757","12758","12538","12539","12540","12542","12543","12598","12596","12597","12649","12615","12616","12617","12706","13653","13654","13655","13056","13058","13048","13049","13050","S12925","13444","S12931","S12927","S12929","13112","12717","12718","12719","12692","12693","12694","12710","13079","13080","12720","12721","12726","12723","12725","12759","12760","12761","12737","12738","12739","12740","13674","12917","12727","12728","12729","12730","13599","13600","13601","14413","14414","14415","12586","12705","12669","12670","12905","12683","12697","12698","12702","12505","12506","12508","12431","12432","12715","12434","12435","12442","12443","13084","13063","13062","13163","13164","12591","12600","12664","13069","13087","13152","13153","13154","13165","13171","13580","12578","12579","12580","12582","12583","12584","12585","12528","12529","12530","12531","12533","12535","12536","12537","12574","13631","13632","13633","13634","13635","13636","13637","13054","12704","A10279","A10280","C00013","S13030"];
const ORDER_MAP=(()=>{const m={};CODE_ORDER.forEach((c,i)=>m[String(c)]=i);return m;})();
const orderRank=(code)=>{const r=ORDER_MAP[String(code)];return r===undefined?1e9:r;};
const byFixedOrder=(a,b)=>{const ra=orderRank(a.code),rb=orderRank(b.code);if(ra!==rb)return ra-rb;const na=Number(a.code),nb=Number(b.code);if(!isNaN(na)&&!isNaN(nb))return na-nb;return String(a.code).localeCompare(String(b.code));};
const SLOT_META={
  stock:{cycle:"주중 매일 갱신",fmt:".xls(HTML)",dropSub:"현재고조회 다운로드 파일",bar:"bg-blue-500",dot:"bg-blue-500",cyc:"text-blue-600",ring:"ring-blue-400"},
  salesDaily:{cycle:"매일 누적 기록",fmt:".xls(HTML)",dropSub:"일별 매출통계 (파일명 날짜 인식)",bar:"bg-cyan-500",dot:"bg-cyan-500",cyc:"text-cyan-600",ring:"ring-cyan-400"},
  sales:{cycle:"일별 기록 없을 때만 사용",fmt:".xls(HTML)",dropSub:"어드민상품매출통계 파일",bar:"bg-emerald-500",dot:"bg-emerald-500",cyc:"text-emerald-600",ring:"ring-emerald-400"},
  cost:{cycle:"변동 시 재등록",fmt:".xlsx",dropSub:"품목등록(단가 마스터) 파일",bar:"bg-emerald-500",dot:"bg-emerald-500",cyc:"text-emerald-600",ring:"ring-emerald-400"},
  plan:{cycle:"발주 수시 등록",fmt:".xlsx",dropSub:"구매계획(입고 예정) 파일",bar:"bg-amber-500",dot:"bg-amber-500",cyc:"text-amber-600",ring:"ring-amber-400"},
};
const std=(arr)=>{const a=arr.filter(x=>typeof x==="number"&&isFinite(x));if(a.length<2)return null;const m=a.reduce((s,x)=>s+x,0)/a.length;return Math.sqrt(a.reduce((s,x)=>s+(x-m)*(x-m),0)/(a.length-1));};
const avg=(arr)=>{const a=arr.filter(x=>typeof x==="number"&&isFinite(x));return a.length?a.reduce((s,x)=>s+x,0)/a.length:null;};

// Excel style helpers
const GREEN={rgb:"1A6B45"}, ORANGE={rgb:"EA580C"}, WHITE={rgb:"FFFFFF"};
const hdrFont=(bold=true)=>({name:"Arial",bold,sz:10,color:WHITE});
const hdrFill=(orange=false)=>({patternType:"solid",fgColor:orange?ORANGE:GREEN});
const hdrAlign={horizontal:"center",vertical:"center",wrapText:true};
const thinBorder={style:"thin",color:{rgb:"DDDDDD"}};
const cellBorder={bottom:thinBorder,right:thinBorder};
const boldFont={name:"Arial",bold:true,sz:10};
const baseFont={name:"Arial",sz:10};
const numFmt="#,##0"; const decFmt="#,##0.0";

function styleSheet(ws, colWidths=[], headerRow=0, opts={}){
  if(!ws["!ref"]) return;
  const colColors=opts.colColors||{};   // {colIndex: "RRGGBB"}  header fill
  const tintColors=opts.tintColors||{}; // {colIndex: "RRGGBB"}  light body tint
  const range=XLSXStyle.utils.decode_range(ws["!ref"]);
  for(let R=range.s.r;R<=range.e.r;R++){
    for(let C=range.s.c;C<=range.e.c;C++){
      const addr=XLSXStyle.utils.encode_cell({r:R,c:C});
      if(!ws[addr]) ws[addr]={v:"",t:"s"};
      const isHdr=R===headerRow;
      let fill={patternType:"none"};
      if(isHdr){fill={patternType:"solid",fgColor:{rgb:colColors[C]||"1A6B45"}};}
      else if(tintColors[C]){fill={patternType:"solid",fgColor:{rgb:tintColors[C]}};}
      ws[addr].s={
        font:isHdr?hdrFont():baseFont,
        fill,
        alignment:isHdr?hdrAlign:{vertical:"center"},
        border:cellBorder,
        numFmt:(typeof ws[addr].v==="number"&&!isHdr)?(Number.isInteger(ws[addr].v)?numFmt:decFmt):undefined,
      };
    }
  }
  if(colWidths.length) ws["!cols"]=colWidths.map(w=>({wch:w}));
  ws["!freeze"]={xSplit:0,ySplit:headerRow+1,topLeftCell:XLSXStyle.utils.encode_cell({r:headerRow+1,c:0})};
}

// 일별 판매 추세 차트 색/포인트 팔레트 (선택 칩과 그래프 선 색을 맞추기 위해 모듈 상수로 분리)
const TREND_COLORS=["#185FA5","#1D9E75","#BA7517","#C026D3","#E24B4A","#D97706","#0D9488","#7C3AED","#639922","#D85A30"];
const TREND_POINTS=["circle","rect","triangle","rectRot","cross","star","line","circle","rect","triangle"];
const TREND_MAX=10; // 한 그래프에 겹쳐 볼 수 있는 최대 품목 수(가독성 한계)

// 단종 상품: 재고리스트에서 영구 제외 (엑셀 재업로드 시에도 미표시)
const DISCONTINUED_CODES=new Set(["13064","13065","13066","13067"]); // 안보이는 양말 4종
const DISCONTINUED_NAME_RE=/안보이는\s*양말/;
const isDiscontinued=(code,name)=>DISCONTINUED_CODES.has(String(code).trim())||DISCONTINUED_NAME_RE.test(String(name||""));

// 같은 제품이 상품코드 두 개로 나뉘어 등록된 경우 — 왼쪽(흡수되는 구 코드)을 오른쪽(대표 코드)으로 합친다.
// 재고·판매·발주·단가가 모두 대표 코드 한 행으로 합산되고, 목록에는 대표 코드만 남는다.
// 방향을 바꾸려면 좌우를 뒤집으면 된다. 예) "13048":"12640" 으로 쓰면 이너홈 코드가 대표가 된다.
const CODE_ALIASES={
  "12640":"13048", // 이너홈 긴손목 380mm 소형 화이트 → 더롱 튼튼 니트릴 고무장갑 S(소형)
  "12641":"13049", // 〃 중형 → 더롱 M(중형)
  "12642":"13050", // 〃 대형 → 더롱 L(대형)
  "S13030":"13674", // 이너홈 알카라인 건전지 AA 20입 + AAA 20입 — S코드 판매·재고·발주를 13674로 합산 (2026-10-06)
};
const canonCode=(code)=>{const c=String(code||"").trim();return CODE_ALIASES[c]||c;};
const HAS_ALIAS=Object.keys(CODE_ALIASES).length>0;
// 대표 코드 → 흡수된 구 코드들 (단가처럼 구 코드에만 값이 있는 경우를 되찾기 위해 필요)
const ALIAS_SOURCES={};
Object.entries(CODE_ALIASES).forEach(([from,to])=>{(ALIAS_SOURCES[to]=ALIAS_SOURCES[to]||[]).push(from);});
// 매칭표도 별칭을 반영해 재구성 — 대표 코드가 구 코드의 A코드·상품명을 물려받고, 구 코드는 표에서 빠진다.
const ACODE_RESOLVED={...ACODE_MAP};
const NAMES_RESOLVED={...PRODUCT_NAMES};
Object.entries(CODE_ALIASES).forEach(([from,to])=>{
  if(!ACODE_RESOLVED[to]&&ACODE_RESOLVED[from])ACODE_RESOLVED[to]=ACODE_RESOLVED[from];
  if(!NAMES_RESOLVED[to]&&NAMES_RESOLVED[from])NAMES_RESOLVED[to]=NAMES_RESOLVED[from];
  delete ACODE_RESOLVED[from];
});

// 예외적으로 표시할 S코드 상품 (니트릴 고무장갑 화이트 시리즈) — 재고관리·발주산출 모두 반영
const INCLUDED_S_CODES=new Set(["S12925","S12931","S12927","S12929","S13030"]);
const isExcludedSCode=(code)=>{const c=String(code).trim().toUpperCase();return c.startsWith("S")&&!INCLUDED_S_CODES.has(c);};

// 수동 A코드 매칭 (현재고 엑셀 '상품메모'가 비어있거나 어긋난 경우 여기서 보정) — 상품코드→이카운트 A코드
// 우선순위: ACODE_OVERRIDES(여기) > 현재고 엑셀 '상품메모' > ACODE_MAP(acodeMap.js 매칭표).
// 대량 매칭표는 acodeMap.js에 따로 두고, 여기에는 그 표로도 해결이 안 되는 예외만 손으로 넣는다.
const ACODE_OVERRIDES={
  "S13030":"A10419",
  "13599":"A10547", // 이너홈 위생접시 14cm 15입
  "13600":"A10548", // 이너홈 위생접시 18cm 15입
  "13601":"A10549", // 이너홈 위생접시 23cm 15입
};

// A코드 별칭 — 한 품목에 이카운트 A코드가 둘 이상 붙어 있는 경우, 여분의 A코드를 그 상품코드로 흡수한다.
// 대표 A코드(화면에 표시되는 값)는 매칭표·상품메모가 정하고, 여기 적은 A코드는 "발주 물량을 받아줄 통로"로만 쓴다.
// 이게 없으면 발주 앱이 여분 A코드로 보낸 물량이 이름 없는 별도 행으로 뜬다.
const ACODE_ALIASES={
  // (현재 없음) 예) "A10423":"12711" 처럼 적으면 그 A코드로 온 발주가 해당 상품코드로 합류한다.
};

// 상품명 강제 변경 — 현재고 엑셀(ERP)의 이름과 달라도 여기 적은 이름을 화면에 표시한다 (2026-10-06)
const NAME_OVERRIDES={
  "13638":"이너홈 니트릴장갑 화이트 100매 특대",
  "13643":"이너홈 니트릴장갑 블랙 100매 특대",
  "13642":"이너홈 니트릴장갑 블루 100매 특대",
};

// 현재고 엑셀에 아직 없는 수동 등록 품목의 표시용 이름 (엑셀에 들어오면 엑셀 값이 우선)
const MANUAL_NAMES={
  "13599":"이너홈 위생접시 14cm 15입",
  "13600":"이너홈 위생접시 18cm 15입",
  "13601":"이너홈 위생접시 23cm 15입",
};

// 수동 A코드 매칭을 등록한 품목은 의도적으로 관리 대상에 넣은 것이므로
// 현재고·판매·발주가 모두 0이어도 재고관리 목록에 항상 표시한다(매칭 결과를 눈으로 확인 가능).
// ⚠️ ACODE_MAP(매칭표 300건)은 여기에 포함하지 않는다 — 매칭 전용이라 목록에 다 띄우면 화면이 넘친다.
const ALWAYS_SHOW_CODES=new Set(Object.keys(ACODE_OVERRIDES));

// 수기 판매 보정 — 일별 판매 엑셀에 빠진 출고분을 여기서 가산/차감한다.
// 형식: { "YYYY-MM-DD": { 상품코드: 가감수량 } }  (양수=판매 가산, 음수=차감)
// 이 보정은 salesHistory를 읽는 모든 화면(재고관리 월판매량 · 일일 판매 · 발주 산출)에 동시에 반영된다.
// 저장(localStorage·클라우드)에는 원본만 남으므로 같은 날짜를 재업로드해도 중복 가산되지 않는다.
const SALES_ADJUSTMENTS={
  "2026-07-31":{"12620":1812}, // 7/31 당일 출고 1,812개 — 일별 판매 엑셀 누락분 보완
  "2026-09-22":{"12523":1800,"12522":7200}, // 9/21 니트릴장갑 20,000개 입고 후 9/22 대량 출고 누락분(기록재고 19,997/19,892 → 현재고 18,197/12,692)
};

// 수기 현재고 보정 — 현재고 엑셀(ERP)에 아직 반영되지 않은 출고를 화면 재고에 맞춘다.
// 형식: { "현재고 기준일(YYYY-MM-DD)": { 상품코드: 실제 현재고 } }  (가감이 아니라 값 지정)
// 해당 기준일의 현재고 파일을 보고 있을 때만 적용 → 다음 날짜 파일을 올리면 자동으로 ERP 값으로 돌아간다(이중 차감 방지).
const STOCK_ADJUSTMENTS={
  "2026-09-22":{"12523":18197,"12522":12692}, // 니트릴장갑 블랙 소형/중형 9/22 출고 1,800/7,200 ERP 미반영분
  // 알카라인 건전지 6품목 — 10/1 현재고 이상치(189,294 등)를 9/30 재고로 고정 (사용자 지시 2026-10-01)
  "2026-10-01":{"12739":-4,"12740":-443,"12738":4358,"12737":2137,"S13030":0,"13674":-6},
};

// 재고 이력(스냅샷) 보정 — 특정 날짜 스냅샷의 품목 수량을 다른 날짜 값으로 덮어쓰고,
// 그 차이(수량차 × 입고단가)만큼 그날 재고금액(valueHistory)도 함께 고친다.
// 관리자 접속 시 클라우드 이력을 다 불러온 뒤 1회 실행되어 Firestore에 직접 저장된다(이미 같으면 아무것도 안 함 → 중복 보정 없음).
const HISTORY_FIXES=[
  {date:"2026-10-01",from:"2026-09-30",codes:["12739","12740","12738","12737","S13030","13674"]}, // 알카라인 건전지 10/1 이상치
];
function applyStockAdjust(raw,stockDate){
  const adj=STOCK_ADJUSTMENTS[String(stockDate||"").slice(0,10)];
  if(!adj||!raw||!raw.stock)return raw;
  return {...raw,stock:raw.stock.map(r=>adj[r.code]!=null?{...r,stock:adj[r.code]}:r)};
}

// 보정 대상 날짜의 기록이 아직 없으면 적용하지 않는다.
// (없는 날짜를 새로 만들면 그날 다른 품목이 전부 판매 0으로 잡혀 최근 30일 집계가 왜곡됨)
function applySalesAdjust(sh){
  const days=Object.keys(SALES_ADJUSTMENTS);
  if(!days.length||!sh||Object.keys(sh).length===0)return sh;
  let touched=false;
  const out={...sh};
  days.forEach(d=>{
    if(!out[d])return; // 해당 날짜 일별 판매 기록 없음 → 건너뜀
    const adj=SALES_ADJUSTMENTS[d];
    const day={...out[d]};
    Object.keys(adj).forEach(code=>{
      const base=typeof day[code]==="number"?day[code]:0;
      day[code]=base+adj[code];
    });
    out[d]=day;touched=true;
  });
  return touched?out:sh;
}

// 보정이 실제로 적용됐는지 화면에 알리기 위한 목록
function pendingSalesAdjust(sh){
  return Object.keys(SALES_ADJUSTMENTS).filter(d=>!sh||!sh[d]);
}

// ===== 입항 일자(구매계획 이력의 date) 처리 유틸 =====
// 발주 계획 앱 purchasePlans 문서의 날짜 필드는 issueDate(발행일) · date(입항 일자) · confirmedAt(확정일시) 뿐이다.
// 별도의 입항일 필드는 존재하지 않으므로 재고·발주 쪽 '입항 일자'는 항상 p.date 하나를 쓴다.
const ymdLocal=(d)=>`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const addDaysYmd=(ymd,days)=>{const d=new Date(ymd+"T00:00:00");if(isNaN(d.getTime()))return ymd;d.setDate(d.getDate()+days);return ymdLocal(d);};
// 발주잔량을 입항 일자 기준으로 '리드타임 안에 들어오는 물량(within)'과 '그 뒤에 들어올 물량(later)'으로 나눈다.
// 입항 일자가 없는 발주(구매계획에 날짜 미입력)는 시점을 알 수 없어 기존 동작대로 within에 넣는다.
// 이미 지난 입항 예정일은 아직 미입고 상태이므로 within으로 본다.
function splitInboundByLT(it,lt,todayYmd){
  const total=Number(it.order)||0;
  const inb=it.inbound||{};
  const horizon=addDaysYmd(todayYmd,lt);
  let within=0,dated=0;
  Object.keys(inb).forEach(d=>{
    const q=Number(inb[d])||0;
    if(!q)return;
    dated+=q;
    if(!/^\d{4}-\d{2}-\d{2}$/.test(d)||d<=horizon)within+=q;
  });
  within+=Math.max(0,total-dated); // 날짜가 붙지 않은 잔량
  if(within>total)within=total;
  return{within,later:Math.max(0,total-within)};
}

function buildItemsFromRaw(raw,overrides,warnDays,overMonths,planDates,dailyMonthly){
  const map=new Map();
  const g=code=>{if(!map.has(code))map.set(code,{code,name:"",acode:"",stock:null,monthly:null,daily:null,order:0,inbound:{}});return map.get(code);};
  // 별칭이 걸린 코드는 대표 코드로 흡수하며 재고·판매를 합산한다(같은 제품이 두 코드로 나뉜 경우).
  (raw.stock||[]).forEach(s=>{const it=g(canonCode(s.code));it.name=it.name||s.name;if(s.acode)it.acode=s.acode;it.stock=(it.stock??0)+(s.stock??0);});
  (raw.sales||[]).forEach(s=>{const it=g(canonCode(s.code));it.name=it.name||s.name;it.monthly=(it.monthly??0)+(s.monthly??0);if(s.daily!=null)it.daily=(it.daily??0)+s.daily;});
  map.forEach(it=>{if(!it.acode&&ACODE_RESOLVED[it.code])it.acode=ACODE_RESOLVED[it.code];}); // 매칭표 보충(엑셀 '상품메모'가 비어 있을 때만)
  Object.keys(ACODE_OVERRIDES).forEach(code=>{g(code).acode=ACODE_OVERRIDES[code];}); // 수동 매칭 적용(엑셀·매칭표보다 우선)
  Object.keys(MANUAL_NAMES).forEach(code=>{const it=g(code);if(!it.name)it.name=MANUAL_NAMES[code];}); // 엑셀에 이름이 없을 때만 보완
  // 구매계획(발주) 반영: 발주 앱은 상품코드 자리에 이카운트 A코드(예: A10024)가 들어올 수 있다.
  // 재고(현재고)의 '상품메모'(acode)로 A코드→재고 상품코드를 역매칭해 실시간 재고에 붙인다.
  const acodeIndex=new Map();
  // ① 매칭표를 씨앗으로 먼저 깔아둔다. 현재고 엑셀에 없는 품목(재고 0인 신규품 등)도
  //    발주 앱이 A코드로 보낸 물량을 제 상품코드에 붙일 수 있어야 하기 때문.
  Object.keys(ACODE_RESOLVED).forEach(code=>{acodeIndex.set(String(ACODE_RESOLVED[code]).trim().toUpperCase(),code);});
  // ② 실데이터(엑셀 상품메모·수동 보정)가 있으면 그 값으로 덮어쓴다 — 항상 실시간 값이 우선.
  map.forEach((it,code)=>{if(it.acode)acodeIndex.set(String(it.acode).trim().toUpperCase(),code);});
  // ③ 손으로 지정한 A코드 별칭은 맨 마지막 — 여분 A코드로 들어온 발주를 지정한 상품코드가 받아준다.
  Object.keys(ACODE_ALIASES).forEach(a=>{acodeIndex.set(a.trim().toUpperCase(),canonCode(ACODE_ALIASES[a]));});
  (raw.plan||[]).forEach(p=>{
    let key=String(p.code||"").trim();
    const mapped=acodeIndex.get(key.toUpperCase())||(p.acode?acodeIndex.get(String(p.acode).trim().toUpperCase()):null);
    if(mapped)key=mapped; // A코드 → 재고 상품코드로 치환
    key=canonCode(key);   // 별칭이 걸린 구 코드면 대표 코드로 합류
    const it=g(key);
    if(!it.acode)it.acode=p.acode||(mapped?p.code:"");
    it.order=(it.order||0)+(p.order||0);
    Object.keys(p.inbound||{}).forEach(d=>{it.inbound[d]=(it.inbound[d]||0)+p.inbound[d];});
  });
  // 발주 때문에 새로 생긴 행은 이름이 비어 있다 — 매칭표의 상품명으로 채운다(엑셀 이름이 있으면 그대로 둔다).
  map.forEach(it=>{if(!it.name&&NAMES_RESOLVED[it.code])it.name=NAMES_RESOLVED[it.code];});
  // 합쳐진 행은 흡수된 구 코드의 이름이 먼저 들어갔을 수 있다 — 대표 코드의 이름으로 되돌린다.
  Object.values(CODE_ALIASES).forEach(to=>{const it=map.get(to);if(it&&NAMES_RESOLVED[to])it.name=NAMES_RESOLVED[to];});
  Object.keys(NAME_OVERRIDES).forEach(code=>{const it=map.get(code);if(it)it.name=NAME_OVERRIDES[code];}); // 강제 상품명 적용
  const cost=raw.cost||{};
  const out=[];
  map.forEach(it=>{
    if(isExcludedSCode(it.code))return;
    if(isDiscontinued(it.code,it.name))return; // 단종 상품 제외
    const stock=it.stock??0;
    const dm=dailyMonthly&&dailyMonthly[it.code];
    // 일별 판매 기록이 있으면 최근 30일 합계가 월판매량을 전면 대체(미기록 상품은 판매 0), 없으면 엑셀 월별 판매 사용
    const monthly=dailyMonthly?(dm!=null?dm:0):(it.monthly??0);
    if(stock===0&&monthly===0&&!(it.order>0)&&!ALWAYS_SHOW_CODES.has(String(it.code)))return; // 재고·판매가 없어도 구매계획(발주)·수동 등록이면 표시
    const daily=dailyMonthly?monthly/30:(it.daily!=null?it.daily:monthly/30);
    const avail=stock+(it.order||0);
    // 단가는 구 코드에만 등록돼 있을 수 있으므로 별칭 원본까지 되짚어 찾는다.
    let cc=cost[it.code];
    if(cc==null&&ALIAS_SOURCES[it.code])for(const f of ALIAS_SOURCES[it.code]){if(cost[f]!=null){cc=cost[f];break;}}
    const uc=overrides[it.code]!=null&&overrides[it.code]!==""?toNum(overrides[it.code]):(cc!=null?cc:null);
    let status="정상";
    const days=daily>0?avail/daily:null;
    const months=monthly>0?avail/monthly:null;
    // 상태 판정은 발주량을 뺀 현재고 대비 월판매량 기준으로만 판단(가용일수/재고개월수 표시값은 발주 포함 유지)
    const stockDays=daily>0?stock/daily:null;
    const stockMonths=monthly>0?stock/monthly:null;
    if(stock<=0)status="부족"; // 재고 음수·재고 0 포함
    else if(monthly===0)status="과잉";
    else if(stockDays!=null&&stockDays<warnDays)status="부족";
    else if(stockMonths!=null&&stockMonths>=overMonths)status="과잉";
    out.push({...it,stock,monthly,daily,avail,days,months,unitCost:uc,value:uc!=null?stock*uc:null,hasCost:uc!=null,status});
  });
  return out;
}

// 리포트 기간 선택 — 상단 전역 필터와 각 차트 옆 칩이 같은 목록·같은 상태(reportRange)를 쓴다.
// 차트마다 따로 상태를 두면 "지금 보고 있는 게 어느 기간인지"가 어긋나므로 의도적으로 하나로 묶었다.
const REPORT_RANGES=[{v:7,l:"최근 7일"},{v:30,l:"최근 30일"},{v:-1,l:"올해"},{v:0,l:"전체"}];
function RangeChips({value,onChange}){
  return(
    <div className="flex items-center gap-1 flex-wrap">
      {REPORT_RANGES.map(o=>(
        <button key={o.v} onClick={()=>onChange(o.v)}
          className={"px-2 py-1 rounded text-[11px] font-medium transition-colors border "+(value===o.v?"bg-[#0a6ed1] text-white border-[#0a6ed1]":"bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>{o.l}</button>
      ))}
    </div>
  );
}

function ChartCanvas({type,data,options,height=280,ariaLabel}){
  const ref=React.useRef(null);
  const inst=React.useRef(null);
  React.useEffect(()=>{
    if(!ref.current)return;
    if(inst.current){inst.current.destroy();}
    inst.current=new Chart(ref.current,{type,data,options});
    return ()=>{if(inst.current)inst.current.destroy();};
  },[type,data,options]);
  return <div style={{position:"relative",width:"100%",height:height+"px"}}><canvas ref={ref} role="img" aria-label={ariaLabel}/></div>;
}

// 위/아래 양쪽에 가로 스크롤바가 있는 래퍼 (두 스크롤 위치 동기화)
// 표 탭 목록 — 이 탭들은 표 박스가 화면 높이를 차지하고 표 안에서만 세로 스크롤된다(헤더 고정)
const TABLE_TABS=["manage","value","daily","sales","reorder"];
// 표 박스 전체 높이(화면 높이에서 아래 여백만 뺀 값). 박스는 flex column, 스크롤 영역이 남은 높이를 채운다.
const TABLE_BOX_H="calc(100vh - 16px)";

// fit=true면 부모(flex column)의 남은 높이를 채우는 세로 스크롤 영역이 된다.
function FitScroll({children,className,fit}){
  return <div className={(className||"")+(fit?" flex-1 min-h-0 overflow-y-auto":"")}>{children}</div>;
}

// fit=true면 부모(flex column)의 남은 높이를 채운다.
function ScrollXBoth({children,className,fit}){
  const topRef=React.useRef(null);
  const botRef=React.useRef(null);
  const [w,setW]=React.useState(0);
  React.useEffect(()=>{
    const el=botRef.current;if(!el)return;
    const update=()=>setW(prev=>prev!==el.scrollWidth?el.scrollWidth:prev);
    update();
    const ro=new ResizeObserver(update);ro.observe(el);
    return ()=>ro.disconnect();
  });
  const syncFromTop=()=>{if(botRef.current&&topRef.current)botRef.current.scrollLeft=topRef.current.scrollLeft;};
  const syncFromBot=()=>{if(botRef.current&&topRef.current)topRef.current.scrollLeft=botRef.current.scrollLeft;};
  return(
    <div className={(className||"")+(fit?" flex flex-col min-h-0 flex-1":"")}>
      <div ref={topRef} onScroll={syncFromTop} className="overflow-x-auto shrink-0" style={{overflowY:"hidden"}}>
        <div style={{width:w,height:1}}/>
      </div>
      <div ref={botRef} onScroll={syncFromBot} className={"overflow-x-auto"+(fit?" flex-1 min-h-0 overflow-y-auto":"")}>
        {children}
      </div>
    </div>
  );
}

// ===== 업데이트 공지 게시판 =====
// 새 업데이트는 아래 배열 맨 위에 한 줄 추가하면 된다(배포하면 바로 반영).
//   { date:"YYYY-MM-DD", tag:"신규|개선|수정|안내", text:"한 줄 설명" }
// 최근 NOTICE_DAYS일 이내 항목만 자동 노출되고, 없으면 최신 3건을 대신 보여준다.
// 시장가격 조사 버튼 표시 여부 — true로 바꾸면 다시 나타난다.
const SHOW_MARKET_PRICE = false;

const NOTICE_DAYS = 30;
const UPDATE_NOTICES = [
  { date:"2026-10-06", tag:"수정", text:"니트릴장갑 100매 특대 3종(13638 화이트·13643 블랙·13642 블루) 상품명 변경" },
  { date:"2026-10-06", tag:"개선", text:"재고관리 목록 정렬을 지정한 상품 순서(157개)로 변경 — 목록에 없는 상품은 맨 아래" },
  { date:"2026-10-06", tag:"개선", text:"알카라인 건전지 AA 20입 + AAA 20입(S13030)을 13674로 합산 — 재고·판매·발주를 한 행으로 통합" },
  { date:"2026-10-05", tag:"신규", text:"구매 시뮬레이션 ② — 한 파일로 여러 카테고리를 한 번에 업로드(카테고리·카테고리명 열)" },
  { date:"2026-10-05", tag:"신규", text:"구매 시뮬레이션 ② — 수정값·설정을 클라우드에 자동 저장(관리자)" },
  { date:"2026-10-05", tag:"개선", text:"구매 시뮬레이션 ② — 이번달(M0) 소요량·발주잔량·말재고 열 추가" },
  { date:"2026-10-05", tag:"개선", text:"구매 시뮬레이션 ② — 발주잔량을 납기 월별 입고(M1~M3)로 반영하고 권장 발주량에서 차감" },
  { date:"2026-10-04", tag:"개선", text:"구매 시뮬레이션 ② — 수급 기간을 3개월(M1~M3)로 확장, 표 머리글 글자 축소" },
  { date:"2026-10-01", tag:"수정", text:"알카라인 건전지 6품목의 10/1 현재고 이상치를 9/30 재고로 보정" },
  { date:"2026-09-28", tag:"개선", text:"구매 시뮬레이션 ② — M1 발주량 입력 칸 추가" },
  { date:"2026-09-26", tag:"신규", text:"구매 시뮬레이션 ② — 카테고리별 수급표 신설(기존 시뮬레이션은 ①로 명칭 변경)" },
  { date:"2026-09-26", tag:"개선", text:"구매 시뮬레이션 ② — 기본 리드타임 45일, 표 자체 스크롤·머리글 고정" },
  { date:"2026-09-25", tag:"수정", text:"더블 지퍼백 A코드 정정 — 이너홈 더블 지퍼백(12901~12903)으로 이전" },
  { date:"2026-09-24", tag:"수정", text:"니트릴장갑 블랙 소형·중형의 9/22 대량 출고 누락분을 판매·현재고에 보정" },
  { date:"2026-09-22", tag:"신규", text:"매출매입 > 매출 탭에 1~4분기·상반기·하반기 기간 선택 추가" },
  { date:"2026-09-22", tag:"수정", text:"쿠팡(브랜디노) 매출에도 선택 기간이 적용되도록 수정(기존에는 전 기간 합산)" },
  { date:"2026-09-14", tag:"안내", text:"'시장가격 조사' 버튼을 메뉴에서 숨김" },
  { date:"2026-09-14", tag:"신규", text:"지급기일 관제 — 발주 계획 앱의 지급 스케줄·주간·월간 자금소요를 재고 앱에서 조회" },
  { date:"2026-09-14", tag:"수정", text:"재고관리 표를 가로 스크롤할 때 날짜 열이 고정 열과 겹치던 현상 수정" },
  { date:"2026-09-08", tag:"개선", text:"입고 예정 배치 기준을 발주 앱의 '납기' 필드로 변경" },
];
const NOTICE_TAG_STYLE = {
  신규:"bg-emerald-50 text-emerald-700 border-emerald-200",
  개선:"bg-sky-50 text-sky-700 border-sky-200",
  수정:"bg-amber-50 text-amber-700 border-amber-200",
  안내:"bg-slate-100 text-slate-600 border-slate-200",
};

function UpdateNoticeBoard(){
  const [open,setOpen]=useState(true);
  const { list, recentCount } = useMemo(()=>{
    const today=new Date(); today.setHours(0,0,0,0);
    const limit=today.getTime()-(NOTICE_DAYS-1)*86400000;
    const sorted=[...UPDATE_NOTICES].sort((a,b)=>String(b.date).localeCompare(String(a.date)));
    const recent=sorted.filter(n=>{const t=new Date(n.date+"T00:00:00").getTime();return isFinite(t)&&t>=limit;});
    return { list: recent.length?recent:sorted.slice(0,3), recentCount: recent.length };
  },[]);
  const isNew=(d)=>{
    const today=new Date(); today.setHours(0,0,0,0);
    const t=new Date(d+"T00:00:00").getTime();
    return isFinite(t)&&t>=today.getTime()-2*86400000;
  };
  return (
    <aside className="h-full flex flex-col rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <button
        onClick={()=>setOpen(o=>!o)}
        className="w-full flex items-center gap-2 px-3.5 py-2.5 bg-slate-50 border-b border-slate-200 hover:bg-slate-100 transition-colors"
        title="업데이트 공지 접기/펼치기"
      >
        <Megaphone className="h-4 w-4 text-[#0a6ed1] shrink-0"/>
        <span className="text-sm font-semibold text-slate-700">업데이트 공지</span>
        <span className="ml-auto text-[11px] font-medium text-slate-400 whitespace-nowrap">
          최근 {NOTICE_DAYS}일 {recentCount}건
        </span>
        <ChevronDown className={"h-3.5 w-3.5 text-slate-400 shrink-0 transition-transform "+(open?"":"-rotate-90")}/>
      </button>
      {open&&(
        <div className="flex-1 min-h-0 overflow-y-auto">
          {recentCount===0&&(
            <div className="px-3.5 py-1.5 text-[11px] text-slate-400 bg-slate-50/70 border-b border-slate-100">
              최근 {NOTICE_DAYS}일 내 업데이트 없음 — 이전 내역
            </div>
          )}
          <ul className="divide-y divide-slate-100">
            {list.map((n,i)=>(
              <li key={n.date+i} className="px-3.5 py-2.5 hover:bg-slate-50/70 transition-colors">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={"inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold "+(NOTICE_TAG_STYLE[n.tag]||NOTICE_TAG_STYLE.안내)}>{n.tag}</span>
                  <span className="text-[11px] font-medium tabular-nums text-slate-400">{n.date.slice(5).replace("-",".")}</span>
                  {isNew(n.date)&&<span className="text-[10px] font-bold text-rose-500">NEW</span>}
                </div>
                <p className="text-xs leading-relaxed text-slate-600">{n.text}</p>
              </li>
            ))}
            {list.length===0&&<li className="px-3.5 py-6 text-center text-xs text-slate-400">등록된 공지가 없습니다</li>}
          </ul>
        </div>
      )}
    </aside>
  );
}

export default function App() {
  const [raw,setRaw]=useState({stock:null,sales:null,cost:null,plan:null});
  const [planDates,setPlanDates]=useState([]);
  const [overrides,setOverrides]=useState({});
  const [history,setHistory]=useState({});
  const [valueHistory,setValueHistory]=useState({});
  const [salesHistoryRaw,setSalesHistory]=useState({});
  // 화면·계산에 쓰이는 판매 기록 = 업로드 원본 + 수기 보정(SALES_ADJUSTMENTS)
  // 저장/업로드 병합에는 salesHistoryRaw(원본)를 쓴다.
  const salesHistory=useMemo(()=>applySalesAdjust(salesHistoryRaw),[salesHistoryRaw]);
  const salesAdjustMissing=useMemo(()=>pendingSalesAdjust(salesHistoryRaw),[salesHistoryRaw]);
  const pendingSlot=useRef(null);
  const reportRef=useRef(null);
  const [meta,setMeta]=useState({stockDate:"",counts:{},uploadedAt:{}});
  const [warnDays,setWarnDays]=useState(30);
  const [overMonths,setOverMonths]=useState(6);
  const [loaded,setLoaded]=useState(false);
  const [histLoaded,setHistLoaded]=useState(false); // 클라우드 이력 1회 로드 완료 여부 (HISTORY_FIXES 실행 조건)
  const [tab,setTab]=useState("manage");
  const [reportRange,setReportRange]=useState(0); // 0=전체, 7, 30, -1=올해
  const [search,setSearch]=useState("");
  // 리포트 '일별 판매 추세' 전용 — 아이템 검색어와 직접 고른 품목 (비어 있으면 판매 상위 10 표시)
  const [trendSearch,setTrendSearch]=useState("");
  const [trendPicked,setTrendPicked]=useState([]);
  const [showHistory,setShowHistory]=useState(false);
  const [showVendorSales,setShowVendorSales]=useState(false);
  const [showNaverInsight,setShowNaverInsight]=useState(false);
  const [showWeeklyReport,setShowWeeklyReport]=useState(false);
  // 발주 산출에서 체크한 품목만 추려 월평균·리드타임을 바꿔보는 구매 시뮬레이션 오버레이
  const [showSimulation,setShowSimulation]=useState(false);
  const [showSimulation2,setShowSimulation2]=useState(false); // 구매 시뮬레이션 ② (카테고리별)
  const [showPaymentDue,setShowPaymentDue]=useState(false);
  const [planSyncedAt,setPlanSyncedAt]=useState("");
  const [planSyncErr,setPlanSyncErr]=useState("");
  const [costSyncedAt,setCostSyncedAt]=useState("");
  const [costSyncErr,setCostSyncErr]=useState("");
  const [showSettings,setShowSettings]=useState(false);
  // 주간 경영관리 리포트 발송 설정 (settings/reportConfig, 관리자 전용)
  const [reportCfg,setReportCfg]=useState({recipients:"",gmailUser:"",gmailAppPassword:"",enabled:true});
  const [reportCfgLoaded,setReportCfgLoaded]=useState(false);
  const [reportSaving,setReportSaving]=useState(false);
  const [testSending,setTestSending]=useState(false);
  // 오늘의 환율 (open.er-api.com — 키 불필요, 1일 1회 갱신)
  const [fx,setFx]=useState(null);
  const [fxErr,setFxErr]=useState("");
  // 발주 산출: 품목별 리드타임(일) · 계산 설정(서비스레벨/목표재고일수/기본리드타임)
  const [leadTimes,setLeadTimes]=useState({});
  const [reorderCfg,setReorderCfg]=useState({svc:95,targetDays:30,defaultLT:14});
  const [onlyOrder,setOnlyOrder]=useState(false);
  // 발주 산출에서 사용자가 직접 고른 품목 (선택 대상 엑셀용) — 화면 전환 시 유지, 저장은 하지 않음
  const [pickedCodes,setPickedCodes]=useState([]);
  // 판매 이력이 없는 품목(일평균 0)에 가정 판매량을 수동 입력해 적정 구매량을 시뮬레이션
  const [dailyOverrides,setDailyOverrides]=useState({});
  // 월말 자동 스냅샷 사용 여부 (기본 켜짐) — 매출원가 계산에 필요한 기말재고 누락 방지
  const [autoMonthEndSnap,setAutoMonthEndSnapState]=useState(true);
  // 현금흐름 관리 (매출매입 화면 '현금흐름' 탭): 실제 통장 잔액을 기준점으로 삼고,
  // 구매계획/쿠팡 매출에 안 잡히는 고정비(임대료·급여·세금 등)를 수동으로 반영한다.
  const [cashActualBalance,setCashActualBalanceState]=useState(0);
  const [cashActualBalanceDate,setCashActualBalanceDateState]=useState("");
  const [fixedCosts,setFixedCostsState]=useState([]); // [{id,name,amount,recurring,month}]
  // 기타 입금(정부지원 대출·보조금·투자금 등 — 매출 입금으로는 안 잡히는 현금 유입). 구조는 fixedCosts와 동일.
  const [otherInflows,setOtherInflowsState]=useState([]); // [{id,name,amount,recurring,month}]
  // 브랜디노 비서(외부 도구)에서 확인하는 쿠팡 로켓그로스·제트배송 일별 매출 — 이카운트 판매현황 엑셀에는
  // 안 잡히는 채널이라 매출매입 화면에서 수동 입력한다. vendorName에 "쿠팡"을 포함시켜 저장하므로
  // 현금흐름 계산의 기존 쿠팡 지급기준(70%@+60일·30%@+90일)이 그대로 적용된다.
  const [brandinoSales,setBrandinoSalesState]=useState([]); // [{id,channel:"rocketGrowth"|"jet",date,amount}]
  const [msg,setMsg]=useState(null);
  const fileRef=useRef(null);
  const [user,setUser]=useState(null);
  const [authReady,setAuthReady]=useState(false);
  const [syncing,setSyncing]=useState(false);
  const applyingRemote=useRef(false);
  // 클라우드 데이터가 한 번이라도 적용됐는지 — 뒤늦게 끝난 로컬 캐시 복원이 클라우드 값을 덮어쓰는 것을 방지
  const remoteApplied=useRef(false);
  // 관리자(쓰기 권한) 판별 — 실제 잠금은 Firestore 보안 규칙(firestore.rules)이 담당하고,
  // 아래 isAdmin은 UI 편의용 분기입니다.
  const ADMIN_EMAILS=["sgmklim99@gmail.com"];
  const isAdmin=!!user&&!user.isAnonymous&&ADMIN_EMAILS.includes((user.email||"").toLowerCase());

  // 설정 창을 열면(관리자만) 저장된 리포트 발송 설정을 1회 불러온다.
  useEffect(()=>{
    if(!showSettings||!isAdmin||reportCfgLoaded)return;
    (async()=>{
      try{
        const cfg=await getReportConfig();
        if(cfg)setReportCfg({recipients:(cfg.recipients||[]).join(", "),gmailUser:cfg.gmailUser||"",gmailAppPassword:cfg.gmailAppPassword||"",enabled:cfg.enabled!==false});
      }catch(e){}
      finally{setReportCfgLoaded(true);}
    })();
  },[showSettings,isAdmin,reportCfgLoaded]);

  const saveReportSettings=async()=>{
    setReportSaving(true);
    try{
      await saveReportConfig({
        recipients:reportCfg.recipients.split(/[,\n]/).map(s=>s.trim()).filter(Boolean),
        gmailUser:reportCfg.gmailUser,
        gmailAppPassword:reportCfg.gmailAppPassword,
        enabled:reportCfg.enabled,
      });
      flash("주간 리포트 발송 설정을 저장했습니다.","success");
    }catch(e){flash("설정 저장 실패: "+(e.message||String(e)),"error");}
    finally{setReportSaving(false);}
  };

  const testSendReport=async()=>{
    setTestSending(true);
    try{
      const fns=getFunctions(app,"asia-northeast3");
      const res=await httpsCallable(fns,"sendWeeklyReportNow",{timeout:120000})();
      const data=res.data||{};
      if(data.sent)flash(`테스트 발송 완료 (수신자 ${(data.recipients||[]).length}명)`,"success");
      else flash("발송되지 않았습니다: "+(data.reason||"수신자/Gmail 설정을 확인하세요"),"error");
    }catch(e){flash("테스트 발송 실패: "+(e.message||String(e)),"error");}
    finally{setTestSending(false);}
  };

  useEffect(()=>{
    (async()=>{
      try{
        const v=localStorage.getItem(KEY);
        if(v){const d=JSON.parse(v);setRaw(d.raw||{stock:null,sales:null,cost:null,plan:null});setPlanDates(d.planDates||[]);setOverrides(d.overrides||{});setHistory(d.history||{});setSalesHistory(d.salesHistory||{});setValueHistory(d.valueHistory||{});setMeta(d.meta||{stockDate:"",counts:{},uploadedAt:{}});if(d.warnDays)setWarnDays(d.warnDays);if(d.overMonths)setOverMonths(d.overMonths);if(d.leadTimes)setLeadTimes(d.leadTimes);if(d.reorderCfg)setReorderCfg(prev=>({...prev,...d.reorderCfg}));if(d.dailyOverrides)setDailyOverrides(d.dailyOverrides);if(typeof d.autoMonthEndSnap==="boolean")setAutoMonthEndSnapState(d.autoMonthEndSnap);if(!remoteApplied.current){if(d.cashActualBalance!=null)setCashActualBalanceState(d.cashActualBalance);if(d.cashActualBalanceDate)setCashActualBalanceDateState(d.cashActualBalanceDate);if(d.fixedCosts)setFixedCostsState(d.fixedCosts);if(d.otherInflows)setOtherInflowsState(d.otherInflows);if(d.brandinoSales)setBrandinoSalesState(d.brandinoSales);}}
      }catch(e){}finally{setLoaded(true);}
    })();
  },[]);

  // 구매계획 자동 반영: 발주 계획 앱(project-procurement-plan)의 구매계획 이력을 읽어와
  // raw.plan/planDates에 자동 반영한다. 더 이상 구매계획 엑셀 업로드가 필요하지 않다.
  const syncPlanFromHistory=async()=>{
    try{
      const plans=await fetchProcurementPlans();
      const {recs,dates}=plansToRawPlan(plans);
      setRaw(prev=>{const next={...prev,plan:recs};return next;});
      setPlanDates(dates);
      const now=new Date().toLocaleString("ko-KR",{month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});
      setMeta(prev=>{const next={...prev,counts:{...prev.counts,plan:recs.length},uploadedAt:{...(prev.uploadedAt||{}),plan:now}};return next;});
      setPlanSyncedAt(now);setPlanSyncErr("");
    }catch(e){setPlanSyncErr(e.message||String(e));}
  };
  useEffect(()=>{syncPlanFromHistory();},[]);

  // 오늘의 환율 불러오기
  useEffect(()=>{
    (async()=>{
      try{
        const r=await fetch("https://open.er-api.com/v6/latest/USD");
        const j=await r.json();
        if(j?.result!=="success"||!j?.rates?.KRW)throw new Error("no data");
        const k=j.rates.KRW;
        setFx({
          USD:k,
          JPY100:(k/j.rates.JPY)*100,
          EUR:k/j.rates.EUR,
          CNY:k/j.rates.CNY,
          at:new Date((j.time_last_update_unix||0)*1000).toLocaleDateString("ko-KR"),
        });
      }catch(e){setFxErr("환율 정보를 불러오지 못했습니다");}
    })();
  },[]);

  // 단가마스터 자동 반영: 단가마스터 앱(price-manager-49b39)의 최신 단가 현황(priceItems)을 읽어와
  // raw.cost(A코드 기준 단가 맵)에 자동 반영한다. 더 이상 단가 마스터 엑셀 업로드가 필요하지 않다.
  const syncCostFromPriceManager=async()=>{
    try{
      const {cost,count,updatedAt}=await fetchLatestPriceCost();
      setRaw(prev=>({...prev,cost}));
      const now=new Date().toLocaleString("ko-KR",{month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});
      setMeta(prev=>({...prev,counts:{...prev.counts,cost:count},uploadedAt:{...(prev.uploadedAt||{}),cost:updatedAt||now}}));
      setCostSyncedAt(updatedAt||now);setCostSyncErr("");
    }catch(e){setCostSyncErr(e.message||String(e));}
  };
  useEffect(()=>{syncCostFromPriceManager();},[]);

  const flash=(text,kind="info")=>{setMsg({text,kind});setTimeout(()=>setMsg(null),3500);};
  const persist=(patch)=>{try{localStorage.setItem(KEY,JSON.stringify({raw,planDates,overrides,history,salesHistory:salesHistoryRaw,valueHistory,meta,warnDays,overMonths,leadTimes,reorderCfg,dailyOverrides,cashActualBalance,cashActualBalanceDate,fixedCosts,otherInflows,brandinoSales,autoMonthEndSnap,...patch}));}catch(e){}};

  // 현재 모든 데이터를 하나의 객체로 모음
  const collectData=()=>({raw,planDates,overrides,history,salesHistory:salesHistoryRaw,valueHistory,meta,warnDays,overMonths,leadTimes,reorderCfg,dailyOverrides,cashActualBalance,cashActualBalanceDate,fixedCosts,otherInflows,brandinoSales,autoMonthEndSnap});

  // 현금흐름 관리 — 관리자만 수정 가능(다른 설정값들과 동일한 권한 체계)
  // 변경 즉시 클라우드(shared 문서)에도 반영해 다른 사용자 화면에 실시간 공유 (관리자만 쓰기 가능)
  const cloudPatch=(patch)=>{if(isAdmin)saveData(patch).catch(()=>{});};
  const setCashActualBalance=(value,date)=>{
    setCashActualBalanceState(value);
    setCashActualBalanceDateState(date);
    persist({cashActualBalance:value,cashActualBalanceDate:date});
    cloudPatch({cashActualBalance:value,cashActualBalanceDate:date});
  };
  const setFixedCosts=(next)=>{setFixedCostsState(next);persist({fixedCosts:next});cloudPatch({fixedCosts:next});};
  const addFixedCost=(name,amount,recurring,month)=>{
    const next=[...fixedCosts,{id:Date.now()+"_"+Math.random().toString(36).slice(2,7),name,amount:toNum(amount),recurring:!!recurring,month:month||""}];
    setFixedCosts(next);
  };
  const removeFixedCost=(id)=>{setFixedCosts(fixedCosts.filter(c=>c.id!==id));};
  const setOtherInflows=(next)=>{setOtherInflowsState(next);persist({otherInflows:next});cloudPatch({otherInflows:next});};
  const setAutoMonthEndSnap=(v)=>{setAutoMonthEndSnapState(v);persist({autoMonthEndSnap:v});cloudPatch({autoMonthEndSnap:v});};
  const addOtherInflow=(name,amount,recurring,month)=>{
    const next=[...otherInflows,{id:Date.now()+"_"+Math.random().toString(36).slice(2,7),name,amount:toNum(amount),recurring:!!recurring,month:month||""}];
    setOtherInflows(next);
  };
  const removeOtherInflow=(id)=>{setOtherInflows(otherInflows.filter(c=>c.id!==id));};
  // 브랜디노 비서 매출은 이제 전부 자동 조회(fetchBrandinoSales)라 새로 입력할 일이 없다.
  // 다만 자동 조회 도입 전에 수동 입력해둔 보정값이 남아 있으면 여전히 자동값보다 우선하므로,
  // 매출매입 화면에서 그 잔여값을 지울 수 있게 삭제 경로만 남겨둔다.
  const setBrandinoSales=(next)=>{setBrandinoSalesState(next);persist({brandinoSales:next});cloudPatch({brandinoSales:next});};
  const removeBrandinoSale=(id)=>{setBrandinoSales(brandinoSales.filter(b=>b.id!==id));};
  // 원격(클라우드) 데이터를 화면에 적용
  const applyData=(d)=>{
    if(!d)return;
    applyingRemote.current=true;
    // ⚠️ 2026-08-27: 클라우드 스냅샷의 raw.plan · planDates는 구조적으로 항상 옛 값이다.
    // 구매계획(입항 일자 포함)은 발주 계획 앱에서 매번 실시간으로 다시 읽어오기 때문이다.
    // 그런데 예전 코드는 스냅샷 값을 먼저 통째로 덮어쓴 뒤 비동기 재동기화로 되돌리는 구조라,
    //  ① 재동기화가 끝나기 전까지 옛 입항 일자가 화면에 보이고,
    //  ② 아래 persist(lightD)가 그 옛 값을 로컬 캐시에 그대로 굳혀버려 다음 실행 때도 옛 날짜로 시작하고,
    //  ③ 발주 앱 조회가 실패하면 옛 날짜가 그대로 남는다.
    // → 스냅샷에서 plan/planDates는 아예 받지 않고, 발주 앱 실시간 값만 쓴다.
    if(d.raw)setRaw(prev=>({...d.raw,plan:(prev&&prev.plan)||d.raw.plan||null}));
    syncPlanFromHistory(); // 구매계획(입항 일자)은 항상 발주 앱 원본에서 실시간으로 다시 읽는다
    syncCostFromPriceManager(); // 클라우드 스냅샷의 cost도 최신이 아닐 수 있으므로 단가마스터 앱에서 다시 실시간 반영
    if(d.overrides)setOverrides(d.overrides);
    // history/salesHistory/valueHistory는 더 이상 이 문서에서 오지 않는다
    // (날짜별 서브컬렉션에서 별도로 1회 로드됨 — 위 useEffect 참고).
    // 예전 문서에 남아있는 옛 필드가 있어도 여기서는 무시해서 최신 값을 덮어쓰지 않는다.
    if(d.meta)setMeta(d.meta);
    if(d.warnDays)setWarnDays(d.warnDays);
    if(d.overMonths)setOverMonths(d.overMonths);
    if(d.leadTimes)setLeadTimes(d.leadTimes);
    if(d.reorderCfg)setReorderCfg(prev=>({...prev,...d.reorderCfg}));
    if(d.dailyOverrides)setDailyOverrides(d.dailyOverrides);
    if(typeof d.autoMonthEndSnap==="boolean")setAutoMonthEndSnapState(d.autoMonthEndSnap);
    // 현금흐름 항목(잔액·고정비·기타 입금)은 관리자가 입력한 클라우드 값이 항상 정답이다.
    // 빈 배열(전체 삭제)·0원도 그대로 반영해야 하므로 존재 여부(!=null)로만 판단한다.
    if(d.cashActualBalance!=null)setCashActualBalanceState(d.cashActualBalance);
    if(d.cashActualBalanceDate!=null)setCashActualBalanceDateState(d.cashActualBalanceDate);
    if(Array.isArray(d.fixedCosts))setFixedCostsState(d.fixedCosts);
    if(Array.isArray(d.otherInflows))setOtherInflowsState(d.otherInflows);
    if(Array.isArray(d.brandinoSales))setBrandinoSalesState(d.brandinoSales);
    // 옛 필드가 남아있어도 로컬 캐시에 덮어쓰지 않음.
    // planDates·raw.plan도 제외해서 스냅샷의 옛 입항 일자가 로컬 캐시에 굳지 않게 한다.
    const{history:_h,salesHistory:_sh,valueHistory:_vh,planDates:_pd,...lightD}=d;
    if(lightD.raw)lightD.raw={...lightD.raw,plan:(raw&&raw.plan)||lightD.raw.plan||null};
    persist(lightD);
    remoteApplied.current=true; // 이후 로컬 캐시 복원이 클라우드 값을 덮어쓰지 못하게 표시
    setTimeout(()=>{applyingRemote.current=false;},100);
  };

  // 인증 상태 감지
  useEffect(()=>{
    const unsub=watchAuth((u)=>{setUser(u);setAuthReady(true);});
    return ()=>unsub&&unsub();
  },[]);

  // 로그인되면 클라우드 데이터 실시간 구독 (가벼운 필드만)
  useEffect(()=>{
    if(!user)return;
    const unsub=watchData((d)=>{applyData(d);});
    return ()=>unsub&&unsub();
  },[user]);

  // 로그인되면 이력 데이터(history/salesHistory/valueHistory)를 1회만 불러옴
  // (실시간 구독 대상이 아니므로 여기서 한 번만 가져와 상태에 채워넣는다)
  useEffect(()=>{
    if(!user)return;
    (async()=>{
      try{
        const [h,sh,vh]=await Promise.all([
          loadAllHistory("history"),
          loadAllHistory("salesHistory"),
          loadAllHistory("valueHistory"),
        ]);
        if(Object.keys(h).length)setHistory(prev=>({...prev,...h}));
        if(Object.keys(sh).length)setSalesHistory(prev=>({...prev,...sh}));
        if(Object.keys(vh).length)setValueHistory(prev=>({...prev,...vh}));
        setHistLoaded(true);
      }catch(e){/* 실패해도 로컬 캐시(localStorage) 값으로 계속 동작 */}
    })();
  },[user]);

  // 로그인 / 로그아웃 핸들러
  const handleGoogleLogin=async()=>{try{await loginWithGoogle();flash("구글 로그인 완료","success");}catch(e){flash("로그인 실패: "+e.message,"error");}};
  const handleAnonLogin=async()=>{try{await loginAnonymously();flash("익명 로그인 완료","success");}catch(e){flash("로그인 실패: "+e.message,"error");}};
  const handleLogout=async()=>{try{await logout();flash("로그아웃 됨","info");}catch(e){flash("로그아웃 실패: "+e.message,"error");}};

  // 브랜디노 비서로 이동 — 이미 로그인된 구글 계정 토큰을 넘겨서
  // 저쪽에서 다시 구글 계정을 물어보지 않고 바로 로그인되게 한다.
  const BRANDINO_URL="https://brandino-agent-280763795837.asia-northeast3.run.app";
  const handleOpenBrandino=async()=>{
    let target=BRANDINO_URL;
    try{
      if(user&&!user.isAnonymous){
        const idToken=await user.getIdToken();
        target=BRANDINO_URL+"/#sso="+encodeURIComponent(idToken);
      }
    }catch(e){/* 토큰을 못 가져와도 그냥 일반 로그인 화면으로 연다 */}
    window.open(target,"_blank","noopener,noreferrer");
  };

  // 클라우드에 수동 저장 (팀 공유)
  const syncToCloud=async()=>{
    if(!user){flash("먼저 로그인하세요","error");return;}
    if(!isAdmin){flash("읽기 전용 계정입니다. 저장은 관리자만 가능합니다.","error");return;}
    try{setSyncing(true);flash("클라우드에 저장하는 중...","info");await saveData(collectData());flash("클라우드 저장 완료 (팀 전체 공유됨)","success");}
    catch(e){flash("클라우드 저장 실패: "+e.message,"error");}
    finally{setSyncing(false);}
  };
  // 클라우드에서 수동 로드
  const loadFromCloud=async()=>{
    if(!user){flash("먼저 로그인하세요","error");return;}
    try{setSyncing(true);flash("클라우드에서 불러오는 중...","info");const d=await loadData();if(d){applyData(d);flash("클라우드에서 불러옴","success");}else{flash("클라우드에 저장된 데이터가 없습니다","info");}}
    catch(e){flash("불러오기 실패: "+e.message,"error");}
    finally{setSyncing(false);}
  };

  const onFile=async(e)=>{
    const file=e.target.files&&e.target.files[0];if(!file)return;
    if(!isAdmin){flash("읽기 전용 계정입니다. 파일 업로드는 관리자만 가능합니다.","error");e.target.value="";return;}
    try{
      const buf=await file.arrayBuffer();
      const bytes=new Uint8Array(buf);
      let isHtml=false;
      for(let i=0;i<Math.min(bytes.length,200);i++){const c=bytes[i];if(c===32||c===9||c===10||c===13||c===239||c===187||c===191)continue;isHtml=(c===60);break;}
      let rows;
      if(isHtml){
        const html=new TextDecoder("utf-8").decode(bytes);
        const doc=new DOMParser().parseFromString(html,"text/html");
        const table=doc.querySelector("table");
        rows=table?Array.from(table.rows).map(tr=>Array.from(tr.cells).map(td=>td.textContent.trim())):[];
      }else{
        const wb=XLSXStyle.read(bytes,{type:"array",cellDates:true});
        const ws=wb.Sheets[wb.SheetNames[0]];
        rows=XLSXStyle.utils.sheet_to_json(ws,{header:1,defval:"",raw:true});
      }
      const hi=findHeaderRow(rows);const rawHdrs=rows[hi]||[];const headers=rawHdrs.map(norm);
      let type=detectType(headers);
      // 같은 양식(월별/일별)은 자동구분 불가 → 누른 슬롯이 일별이면 일별로 처리
      if(pendingSlot.current==="salesDaily"&&type==="sales")type="salesDaily";
      pendingSlot.current=null;
      if(type==="unknown"){flash("파일 종류를 인식하지 못했습니다.","error");return;}
      if(type==="plan"){flash("구매계획은 이제 '구매계획 이력'에서 자동으로 반영됩니다. 엑셀 업로드가 필요 없습니다.","info");e.target.value="";return;}
      const idx=n=>headers.indexOf(n);
      const dataRows=rows.slice(hi+1).filter(r=>r.some(c=>c!==""&&c!=null&&c!==undefined));
      let recs,count;
      let stockExcluded=0;
      if(type==="stock"){
        // 상품명에 '-마켓컬리' 또는 '-쿠팡'(채널 전용 SKU)이 포함된 재고는 제외
        const EXCLUDE_RE=/[-–—]\s*(마켓컬리|쿠팡)/;
        const allStock=dataRows.filter(r=>r[idx("상품코드")]!==""&&r[idx("상품코드")]!=null).map(r=>({code:String(r[idx("상품코드")]).trim(),name:r[idx("상품명")]||"",acode:String(r[idx("상품메모")]??"").trim().replace(/^nan$/i,""),stock:toNum(r[idx("정상재고")])}));
        recs=allStock.filter(rec=>!EXCLUDE_RE.test(rec.name));
        stockExcluded=allStock.length-recs.length;count=recs.length;
      }else if(type==="sales"){
        const di=idx("1일판매수량");
        recs=dataRows.filter(r=>r[idx("상품코드")]!==""&&r[idx("상품코드")]!=null).map(r=>({code:String(r[idx("상품코드")]).trim(),name:r[idx("상품명")]||"",monthly:toNum(r[idx("수량")]),daily:di>=0?toNum(r[di]):null}));count=recs.length;
      }else if(type==="salesDaily"){
        const qi=idx("수량");
        const snap={};
        dataRows.forEach(r=>{const code=String(r[idx("상품코드")]??"").trim();if(code)snap[code]=toNum(r[qi]);});
        const fdate=dateFromFilename(file.name)||new Date().toISOString().slice(0,10);
        const dkey=String(fdate).slice(0,10);
        const nextSH={...salesHistoryRaw,[dkey]:snap}; // 원본 기준 병합(보정값 중복 가산 방지)
        setSalesHistory(nextSH);
        const nowS=new Date().toLocaleString("ko-KR",{month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});
        const nextMetaS={...meta,counts:{...meta.counts,salesDaily:Object.keys(nextSH).length},uploadedAt:{...(meta.uploadedAt||{}),salesDaily:nowS}};
        setMeta(nextMetaS);
        persist({salesHistory:nextSH,meta:nextMetaS});
        if(user){saveHistoryDay("salesHistory",dkey,snap).catch(()=>{});}
        flash(`일별 판매(${dkey}) ${Object.keys(snap).length.toLocaleString("ko-KR")}건 기록 · 누적 ${Object.keys(nextSH).length}일`,"success");
        e.target.value="";return;
      }else if(type==="cost"){
        recs={};const ci=idx("품목코드"),pi=idx("입고단가");dataRows.forEach(r=>{const a=String(r[ci]??"").trim();if(a)recs[a]=toNum(r[pi]);});count=Object.keys(recs).length;
      }
      const nextRaw={...raw,[type]:recs};
      const now=new Date().toLocaleString("ko-KR",{month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});
      const nextMeta={stockDate:type==="stock"?(parseName(file.name)||meta.stockDate):meta.stockDate,counts:{...meta.counts,[type]:count},uploadedAt:{...(meta.uploadedAt||{}),[type]:now}};
      setRaw(nextRaw);setMeta(nextMeta);
      persist({raw:nextRaw,meta:nextMeta});
      flash(`${TYPE_LABEL[type]} ${count.toLocaleString("ko-KR")}건 불러옴${type==="stock"&&stockExcluded>0?` (마켓컬리·쿠팡 ${stockExcluded.toLocaleString("ko-KR")}건 제외)`:""}`,"success");
    }catch(err){flash("파일을 읽는 중 문제가 발생했습니다.","error");}
    finally{e.target.value="";}
  };

  // 일별 판매 기록이 하루라도 있으면 즉시 최근 30일 합계를 월판매량으로 사용(엑셀 월별 판매 대체).
  // 기록이 30일 미만이면 (기간 합계 ÷ 기록일수) × 30 으로 월 환산.
  // 별칭이 걸린 상품코드의 판매 기록을 대표 코드로 합쳐 놓은 사본.
  // 계산(월판매량·발주 산출)에만 쓰고, 일일판매기록 화면의 입력·수정은 원본 salesHistory를 그대로 쓴다.
  const salesHistoryC=useMemo(()=>{
    if(!HAS_ALIAS)return salesHistory;
    const out={};
    Object.keys(salesHistory).forEach(d=>{
      const day={};
      Object.keys(salesHistory[d]).forEach(c=>{
        const k=canonCode(c),v=salesHistory[d][c];
        if(typeof v==="number")day[k]=(day[k]||0)+v;
        else if(day[k]===undefined)day[k]=v;
      });
      out[d]=day;
    });
    return out;
  },[salesHistory]);
  const dailyMonthly=useMemo(()=>{
    const dts=Object.keys(salesHistoryC).sort();
    if(dts.length===0)return null;
    const last30=dts.slice(-30);
    const codes=new Set();last30.forEach(d=>Object.keys(salesHistoryC[d]).forEach(c=>codes.add(c)));
    const out={};
    codes.forEach(code=>{
      let sum=0;
      last30.forEach(d=>{const v=salesHistoryC[d][code];if(typeof v==="number")sum+=v;});
      out[code]=last30.length>=30?sum:Math.round(sum/last30.length*30);
    });
    return out;
  },[salesHistoryC]);
  const rawAdj=useMemo(()=>applyStockAdjust(raw,meta.stockDate),[raw,meta.stockDate]); // 저장은 raw(원본), 계산·표시는 rawAdj
  const items=useMemo(()=>buildItemsFromRaw(rawAdj,overrides,warnDays,overMonths,planDates,dailyMonthly),[rawAdj,overrides,warnDays,overMonths,planDates,dailyMonthly]);
  const adates=useMemo(()=>planDates.filter(d=>items.some(it=>it.inbound&&it.inbound[d])),[planDates,items]);
  const hasAny=raw.stock||raw.sales||raw.cost||(raw.plan&&raw.plan.length>0);
  const match=(it)=>{const q=search.trim().toLowerCase();return!q||it.code.toLowerCase().includes(q)||(it.name||"").toLowerCase().includes(q);};
  const manageRows=useMemo(()=>items.filter(match).sort(byFixedOrder),[items,search]);
  const valueRows=useMemo(()=>items.filter(it=>it.stock!==0).filter(match).sort((a,b)=>(b.value??-1)-(a.value??-1)),[items,search]);
  const totals=useMemo(()=>{let value=0,missing=0,low=0,over=0,stockT=0;items.forEach(it=>{stockT+=it.stock;if(it.hasCost)value+=it.value;else if(it.stock!==0)missing++;if(it.status==="부족")low++;if(it.status==="과잉")over++;});return{value,missing,low,over,stockT};},[items]);
  const histDates=useMemo(()=>Object.keys(history).sort(),[history]);
  const volRows=useMemo(()=>items.map(it=>{const series=histDates.map(d=>history[d]&&history[d][it.code]).filter(x=>typeof x==="number");const m=avg(series),s=std(series);return{code:it.code,name:it.name,n:series.length,mean:m,sd:s,range:series.length?Math.max(...series)-Math.min(...series):null,cv:s!=null&&m?(s/m)*100:null};}),[items,histDates,history]);

  const takeSnapshot=(opts={})=>{
    const {auto=false}=opts;
    if(!isAdmin){if(!auto)flash("읽기 전용 계정입니다. 재고 기록은 관리자만 가능합니다.","error");return false;}
    const date=meta.stockDate?meta.stockDate.slice(0,10):new Date().toISOString().slice(0,10);
    const snap={};let totVal=0;items.forEach(it=>{snap[it.code]=it.stock;if(it.hasCost)totVal+=it.value;});
    const next={...history,[date]:snap};
    const nextV={...valueHistory,[date]:Math.round(totVal)};
    setHistory(next);setValueHistory(nextV);persist({history:next,valueHistory:nextV});
    if(user){
      saveHistoryDay("history",date,snap).catch(()=>{});
      saveHistoryDay("valueHistory",date,Math.round(totVal)).catch(()=>{});
    }
    flash(auto
      ? `월말 자동 스냅샷: ${date} 재고를 기록했습니다 (총 ${won(totVal)})`
      : `${date} 재고 스냅샷 기록 완료 (총 ${won(totVal)})`,"success");
    return true;
  };

  // ===== 월말 자동 스냅샷 =====
  // 기말재고(매출원가 계산의 필수 입력)가 빠지지 않도록, 현재고 데이터의 기준일이
  // 그 달의 마지막 날이면 관리자 접속 시 자동으로 스냅샷을 남긴다.
  // 날짜는 항상 '현재고 파일의 기준일(meta.stockDate)'을 쓰므로, 접속일이 며칠이든
  // 8/2에 7/31자 현재고를 보고 있으면 7-31로 정확히 기록된다.
  const isLastDayOfMonth=(ds)=>{
    const [y,m,d]=String(ds||"").split("-").map(Number);
    if(!y||!m||!d)return false;
    return d===new Date(y,m,0).getDate(); // new Date(y, m, 0) = 그 달의 말일
  };
  const autoSnapRan=useRef(false);
  useEffect(()=>{
    if(autoSnapRan.current)return;
    if(!loaded||!user||!isAdmin||!autoMonthEndSnap)return;
    if(!items.length)return;              // 현재고가 아직 안 올라온 상태
    const date=meta.stockDate?meta.stockDate.slice(0,10):"";
    if(!date||!isLastDayOfMonth(date))return;
    if(history[date])return;              // 이미 그 날짜 기록이 있으면 건너뜀
    autoSnapRan.current=true;
    takeSnapshot({auto:true});
  },[loaded,user,isAdmin,autoMonthEndSnap,items,meta.stockDate,history]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== 재고 이력 보정 (HISTORY_FIXES) =====
  // 클라우드 이력을 다 불러오고 단가가 들어온 뒤에만 실행 — 로컬 캐시 값으로 먼저 돌면 금액 보정이 어긋날 수 있다.
  useEffect(()=>{
    if(!histLoaded||!user||!isAdmin)return;
    if(!items.some(it=>it.hasCost))return; // 단가 동기화 전
    HISTORY_FIXES.forEach(fx=>{
      const to=history[fx.date],src=history[fx.from];
      if(!to||!src)return;
      const diffs=fx.codes.filter(c=>typeof src[c]==="number"&&to[c]!==src[c]);
      if(!diffs.length)return; // 이미 보정됨
      const day={...to};let dv=0;
      diffs.forEach(c=>{
        const it=items.find(i=>String(i.code)===c);
        const before=typeof to[c]==="number"?to[c]:0;
        if(it&&it.hasCost)dv+=it.unitCost*(src[c]-before);
        day[c]=src[c];
      });
      const hasV=typeof valueHistory[fx.date]==="number";
      const nv=hasV?Math.round(valueHistory[fx.date]+dv):null;
      setHistory(prev=>({...prev,[fx.date]:day}));
      if(hasV)setValueHistory(prev=>({...prev,[fx.date]:nv}));
      saveHistoryDay("history",fx.date,day).catch(()=>{});
      if(hasV)saveHistoryDay("valueHistory",fx.date,nv).catch(()=>{});
      flash(`${fx.date} 재고 이력 ${diffs.length}품목을 ${fx.from} 값으로 보정했습니다${hasV?` (재고금액 ${dv>=0?"+":""}${Math.round(dv).toLocaleString("ko-KR")}원)`:""}`,"success");
    });
  },[histLoaded,user,isAdmin,history,valueHistory,items]); // eslint-disable-line react-hooks/exhaustive-deps

  // 월말 스냅샷이 빠진 달 목록 — 배너로 알려 매출원가 계산 공백을 막는다.
  // 기록을 시작하기 전의 달까지 경고하면 소음이 되므로, '첫 기록이 있는 달' 이후의 최근 6개월만 본다.
  const missingMonthEnds=useMemo(()=>{
    const keys=Object.keys(history).sort();
    if(keys.length===0)return [];
    const firstYm=keys[0].slice(0,7);
    const months=new Set(keys.map(k=>k.slice(0,7)));
    const out=[];
    const now=new Date();
    for(let i=6;i>=1;i--){
      const d=new Date(now.getFullYear(),now.getMonth()-i+1,0); // i개월 전 달의 말일
      const ym=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
      if(ym<firstYm)continue;              // 기록 시작 전의 달은 제외
      // 그 달에 기록이 하나라도 있으면 '있음'으로 본다(말일이 휴일이라 하루 이틀 어긋난 경우 포함)
      if(!months.has(ym))out.push(ym);
    }
    return out;
  },[history]);
  // 일일 판매: 하루치 수량 그대로 사용
  const salesDates=useMemo(()=>Object.keys(salesHistory).sort(),[salesHistory]);
  const dailySalesByCode=useMemo(()=>{
    const out={};
    items.forEach(it=>{
      const arr=[];
      salesDates.forEach(d=>{const v=salesHistoryC[d]&&salesHistoryC[d][it.code];if(typeof v==="number")arr.push({date:d,qty:v});});
      out[it.code]=arr;
    });
    return out;
  },[items,salesDates,salesHistoryC]);
  const salesVolRows=useMemo(()=>items.map(it=>{
    const arr=(dailySalesByCode[it.code]||[]).map(x=>x.qty);
    const m=avg(arr),s=std(arr);
    const recent=arr.length?arr[arr.length-1]:null;
    const trend=arr.length>=2?(arr[arr.length-1]-arr[0]):null;
    return{code:it.code,name:it.name,n:arr.length,total:arr.reduce((a,b)=>a+b,0),mean:m,sd:s,cv:s!=null&&m?(s/m)*100:null,recent,trend,monthly:it.monthly};
  }),[items,dailySalesByCode]);

  // ===== 발주 산출 (설계서 수식 · 2026-07-14) =====
  // d̄ = 가중이동평균(최근30일 0.5 / 31~60일 0.3 / 61~90일 0.2, 없는 구간은 가중치 재배분)
  // SS = z × σ_d × √LT (Y등급은 z+0.5) · 목표재고 T = d̄×목표일수+SS
  // 권장발주량 Q = T + d̄×LT − (현재고+발주잔량) · 발주점 ROP = d̄×LT + SS
  const reorderRows=useMemo(()=>{
    const Z_TABLE={90:1.28,95:1.65,99:2.33};
    const dts=salesDates;
    if(dts.length===0)return[];
    const z=Z_TABLE[reorderCfg.svc]||1.65;
    const todayYmd=ymdLocal(new Date()); // 입항 일자 비교 기준일(로컬 날짜)
    // 대상 품목: 재고관리 목록 + (재고 0·판매 0이라 목록에서 빠진) 현재고 파일의 나머지 품목까지 포함
    const seen=new Set();
    const base=[];
    items.forEach(it=>{seen.add(String(it.code));base.push(it);});
    // 별칭이 걸린 코드는 대표 코드로 합산한 뒤 한 번만 추가한다(두 번 세지 않도록).
    const restStock=new Map();
    (raw.stock||[]).forEach(s=>{
      const code=canonCode(s.code);
      if(seen.has(code))return;
      if(isExcludedSCode(code))return;
      if(isDiscontinued(code,s.name))return;
      const prev=restStock.get(code);
      if(prev){prev.stock+=(s.stock||0);if(!prev.name)prev.name=s.name||"";if(!prev.acode)prev.acode=s.acode||"";}
      else restStock.set(code,{code,name:s.name||"",acode:s.acode||"",stock:s.stock||0});
    });
    restStock.forEach(r=>{
      seen.add(r.code);
      base.push({code:r.code,name:NAMES_RESOLVED[r.code]||r.name||"",acode:ACODE_OVERRIDES[r.code]||r.acode||ACODE_RESOLVED[r.code]||"",stock:r.stock,order:0}); // 수동 매칭 > 엑셀 > 매칭표
    });
    const rows=[];
    base.forEach(it=>{
      // 기록된 날짜 전체를 시계열로: 그날 기록이 없으면 판매 0으로 간주(평균 과대평가 방지)
      const series=dts.map(d=>{const v=salesHistoryC[d]&&salesHistoryC[d][it.code];return typeof v==="number"?v:0;});
      const n=series.length;
      const w1=avg(series.slice(Math.max(0,n-30)));
      const w2=n>30?avg(series.slice(Math.max(0,n-60),n-30)):null;
      const w3=n>60?avg(series.slice(Math.max(0,n-90),n-60)):null;
      const parts=[[w1,0.5],[w2,0.3],[w3,0.2]].filter(p=>p[0]!=null);
      const wsum=parts.reduce((s,p)=>s+p[1],0);
      let dAvg=wsum>0?parts.reduce((s,p)=>s+p[0]*p[1],0)/wsum:0;
      let sd=std(series)??0;
      // 수동 입력은 '현재고 0 이하 그리고 판매량 0'인 품목만 허용 → σ=0(변동성 없음)으로 시뮬레이션
      // 재고가 있고 판매량이 0·마이너스인 품목, 소수점 일평균 품목은 모두 실측값으로 자동 계산
      const stockNow=toNum(it.stock);
      const editable=stockNow<=0&&dAvg<=0;
      const ovr=toNum(dailyOverrides[it.code]);
      // 실제 판매가 기록되어 일평균이 0을 넘으면 가정값을 무시하고 자동으로 실측값 사용
      const manual=editable&&ovr>0;
      if(manual){dAvg=ovr;sd=0;}
      const cv=!manual&&dAvg>0?sd/dAvg:null;
      const grade=manual?null:cv==null?null:cv<0.5?"X":cv<=1?"Y":"Z";
      const lt=Math.max(1,toNum(leadTimes[it.code])||reorderCfg.defaultLT);
      const zEff=grade==="Y"?z+0.5:z;
      const ss=dAvg>0?zEff*sd*Math.sqrt(lt):0;
      const rop=dAvg*lt+ss;
      const target=dAvg*reorderCfg.targetDays+ss;
      // 발주잔량을 구매계획 이력의 입항 일자 기준으로 나눈다(2026-08-27).
      // 예전에는 입항 일자와 무관하게 발주잔량 전체를 즉시 가용 재고로 더해서,
      // 두 달 뒤에 들어올 물량까지 "지금 재고가 있다"고 판정 → 실제로 비는 기간에 발주 신호가 뜨지 않았다.
      // 이제 리드타임(LT) 안에 입항하는 물량만 가용으로 인정한다.
      const {within:orderIn,later:orderLater}=splitInboundByLT(it,lt,todayYmd);
      const onhand=(it.stock||0)+orderIn;
      const qty=dAvg>0?Math.max(0,Math.ceil(target+dAvg*lt-onhand)):0;
      const runout=dAvg>0?onhand/dAvg:null;
      let signal="여유";
      if(dAvg>0&&onhand<=rop)signal="지금발주";
      else if(dAvg>0&&onhand<=rop*1.2)signal="곧발주";
      if(dAvg<=0&&onhand<=0&&!editable)return;
      rows.push({code:it.code,name:it.name,acode:it.acode,stock:it.stock||0,order:it.order||0,orderIn,orderLater,onhand,dAvg,sd,cv,grade,lt,ss,rop,target,qty,runout,signal,editable,manual});
    });
    const rank={지금발주:0,곧발주:1,여유:2};
    return rows.sort((a,b)=>rank[a.signal]-rank[b.signal]||((a.runout??1e9)-(b.runout??1e9)));
  },[items,raw.stock,salesDates,salesHistoryC,leadTimes,reorderCfg,dailyOverrides]);
  const setDailyOverride=(code,val)=>{const n={...dailyOverrides};if(val===""||val==null)delete n[code];else n[code]=val;setDailyOverrides(n);persist({dailyOverrides:n});};
  const setLeadTime=(code,val)=>{const n={...leadTimes};if(val===""||val==null)delete n[code];else n[code]=val;setLeadTimes(n);persist({leadTimes:n});};
  const setCfg=(patch)=>{const n={...reorderCfg,...patch};setReorderCfg(n);persist({reorderCfg:n});};

  // 발주 산출 엑셀 내보내기 (발주 대상 / 선택 대상 공용)
  // 발주량이 0인 품목도 내보낼 수 있어야 하므로(선택 대상) 권장발주량 칸은 그대로 0을 내보낸다.
  const exportReorderRows=(rows,kind)=>{
    try{
      if(!rows||rows.length===0){flash(kind==="선택"?"선택된 품목이 없습니다. 표 왼쪽 체크박스로 먼저 선택해 주세요.":"내보낼 발주 대상이 없습니다.","info");return;}
      const wb=XLSXStyle.utils.book_new();
      const hdrs=["상품코드","상품명","A코드","판정","현재고","발주잔량","일평균판매","월평균판매","변동등급","리드타임(일)","안전재고","발주점","권장발주량","실제발주량"];
      const data=rows.map(r=>({상품코드:r.code,상품명:r.name,A코드:r.acode||"",판정:r.signal,현재고:r.stock,발주잔량:r.order,
        일평균판매:Math.round(r.dAvg*10)/10,월평균판매:Math.round(r.dAvg*30),변동등급:r.manual?"수동":(r.grade||""),
        "리드타임(일)":r.lt,안전재고:Math.round(r.ss),발주점:Math.round(r.rop),권장발주량:r.qty,실제발주량:""}));
      const ws=XLSXStyle.utils.json_to_sheet(data,{header:hdrs});
      styleSheet(ws,[10,38,10,9,10,10,11,11,9,11,10,10,12,12],0,{colColors:{12:"EA580C",13:"1A6B45"},tintColors:{12:"FFEDD5",13:"E7F5EC"}});
      XLSXStyle.utils.book_append_sheet(wb,ws,"발주산출");
      XLSXStyle.writeFile(wb,`발주산출_${kind}_${new Date().toISOString().slice(0,10)}.xlsx`);
      flash(`${kind} ${rows.length}품목 내보내기 완료`,"success");
    }catch(e){flash("엑셀 내보내기 실패: "+e.message,"error");}
  };
  // 화면에 실제로 보이는 행(검색·'발주 대상만 보기' 필터 적용) — 전체선택 체크박스와 tbody가 같은 목록을 쓴다
  const reorderVisible=useMemo(()=>{
    const q=search.trim().toLowerCase();
    return reorderRows.filter(r=>
      (!q||String(r.code).toLowerCase().includes(q)||String(r.name||"").toLowerCase().includes(q))
      &&(!onlyOrder||r.qty>0));
  },[reorderRows,search,onlyOrder]);
  const togglePicked=(code)=>setPickedCodes(prev=>{const c=String(code);return prev.includes(c)?prev.filter(x=>x!==c):[...prev,c];});
  const allVisiblePicked=reorderVisible.length>0&&reorderVisible.every(r=>pickedCodes.includes(String(r.code)));
  const toggleAllVisible=()=>{
    const vis=reorderVisible.map(r=>String(r.code));
    setPickedCodes(prev=>allVisiblePicked?prev.filter(c=>!vis.includes(c)):Array.from(new Set([...prev,...vis])));
  };
  const exportReorder=()=>exportReorderRows(reorderRows.filter(r=>r.qty>0),"발주대상");
  const exportReorderPicked=()=>{
    const set=new Set(pickedCodes);
    // 화면 정렬(지금발주 → 곧발주 → 여유) 순서를 그대로 유지해 내보낸다
    exportReorderRows(reorderRows.filter(r=>set.has(String(r.code))),"선택");
  };
  // 구매 시뮬레이션에 넘길 행 — 선택 순서가 아니라 화면 정렬(지금발주→곧발주→여유)을 그대로 유지한다
  const simulationRows=useMemo(()=>{
    const set=new Set(pickedCodes);
    return reorderRows.filter(r=>set.has(String(r.code)));
  },[reorderRows,pickedCodes]);
  const openSimulation=()=>{
    if(pickedCodes.length===0){flash("먼저 표 왼쪽 체크박스로 시뮬레이션할 품목을 선택해 주세요.","info");return;}
    setShowSimulation(true);
  };

  // 리포트 차트용 데이터
  const statusCounts=useMemo(()=>{const c={정상:0,부족:0,과잉:0};items.forEach(it=>{if(c[it.status]!=null)c[it.status]++;});return c;},[items]);
  const topValueItems=useMemo(()=>items.filter(it=>it.hasCost&&it.value>0).sort((a,b)=>b.value-a.value).slice(0,10),[items]);
  const scatterPts=useMemo(()=>items.filter(it=>it.days!=null&&isFinite(it.days)&&it.monthly>0).map(it=>({x:Math.round(it.days*10)/10,y:it.monthly,code:it.code,name:it.name})),[items]);
  // 기간 필터가 적용된 일별 판매 날짜 (reportRange: 7/30=최근 N일, -1=올해, 0=전체)
  const yearStart=useMemo(()=>String(new Date().getFullYear())+"-01-01",[]);
  const rangeLabel=reportRange===0?"전체":reportRange===-1?"올해":"최근 "+reportRange+"일";
  const rangeDates=useMemo(()=>reportRange>0?salesDates.slice(-reportRange):reportRange===-1?salesDates.filter(d=>d>=yearStart):salesDates,[salesDates,reportRange,yearStart]);
  // 기간 내 판매 집계 (상품별 합계/일평균)
  const rangeSales=useMemo(()=>items.map(it=>{
    const vals=rangeDates.map(d=>salesHistoryC[d]&&salesHistoryC[d][it.code]).filter(x=>typeof x==="number");
    const total=vals.reduce((a,b)=>a+b,0);
    return{code:it.code,name:it.name,n:vals.length,total,mean:vals.length?total/vals.length:0};
  }),[items,rangeDates,salesHistoryC]);
  // 일별 판매 추이 상위 10
  const trendTop=useMemo(()=>rangeSales.filter(v=>v.n>=1&&v.total>0).sort((a,b)=>b.total-a.total).slice(0,10),[rangeSales]);
  // 아이템 검색 후보 (판매 0인 품목도 검색되도록 rangeSales 전체를 대상으로 한다)
  const trendCandidates=useMemo(()=>{
    const q=trendSearch.trim().toLowerCase();
    if(!q)return[];
    return rangeSales
      .filter(v=>String(v.code).toLowerCase().includes(q)||String(v.name||"").toLowerCase().includes(q))
      .sort((a,b)=>b.total-a.total).slice(0,30);
  },[rangeSales,trendSearch]);
  // 그래프에 실제로 그릴 시리즈: 고른 품목이 있으면 그것만, 없으면 판매 상위 10
  const trendSeries=useMemo(()=>{
    if(trendPicked.length===0)return trendTop;
    const byCode={};rangeSales.forEach(v=>{byCode[String(v.code)]=v;});
    return trendPicked.map(c=>byCode[c]).filter(Boolean);
  },[trendPicked,rangeSales,trendTop]);
  // setTrendPicked 업데이터 안에서 flash(다른 setState)를 부르지 않도록 한도 검사를 밖에서 처리
  const toggleTrendPick=(code)=>{
    const c=String(code);
    if(trendPicked.includes(c)){setTrendPicked(trendPicked.filter(x=>x!==c));return;}
    if(trendPicked.length>=TREND_MAX){flash(`한 번에 최대 ${TREND_MAX}개까지 겹쳐 볼 수 있습니다.`,"info");return;}
    setTrendPicked([...trendPicked,c]);
  };
  // 일별 판매 합계 (전 품목 합) — 기간 필터 적용
  const dailySalesTotals=useMemo(()=>rangeDates.map(d=>{const day=salesHistory[d]||{};let s=0;Object.keys(day).forEach(c=>{const v=day[c];if(typeof v==="number")s+=v;});return s;}),[rangeDates,salesHistory]);
  // 판매 TOP/하위 표 (기간 내 판매가 있는 상품 기준)
  const salesRanked=useMemo(()=>rangeSales.filter(v=>v.total>0).sort((a,b)=>b.total-a.total),[rangeSales]);
  const salesTop10=useMemo(()=>salesRanked.slice(0,10),[salesRanked]);
  const salesBottom10=useMemo(()=>salesRanked.slice(-10).reverse(),[salesRanked]);
  // 재고자산 카테고리별 구성
  const categoryValue=useMemo(()=>{
    const m={};items.forEach(it=>{if(it.hasCost&&it.value>0){const cat=categoryOf(it.name);m[cat]=(m[cat]||0)+it.value;}});
    return Object.entries(m).sort((a,b)=>b[1]-a[1]);
  },[items]);
  // 일별 총재고금액 변동 — 기간 필터(최근 7일/30일/올해/전체) 적용
  const valueTrend=useMemo(()=>{
    const all=Object.keys(valueHistory).sort();
    const dts=reportRange>0?all.slice(-reportRange):reportRange===-1?all.filter(d=>d>=yearStart):all;
    return{dates:dts,values:dts.map(d=>valueHistory[d]),totalDays:all.length};
  },[valueHistory,reportRange,yearStart]);
  // 월별 총재고금액 (각 월의 마지막 기록일 기준, 최근 12개월)
  const monthlyValueTrend=useMemo(()=>{
    const dts=Object.keys(valueHistory).sort();
    const last={}; // YYYY-MM → {date,value}
    dts.forEach(d=>{const m=d.slice(0,7);last[m]={date:d,value:valueHistory[d]};});
    const ms=Object.keys(last).sort().slice(-12);
    return{months:ms,values:ms.map(m=>last[m].value),basis:ms.map(m=>last[m].date)};
  },[valueHistory]);
  // 일별 총재고금액 차트: 본 차트 + 좌측 금액축 고정 오버레이(동일 설정으로 그려 픽셀 정렬)
  // 최근 7일·30일·올해는 스크롤 없이 한 화면에 맞춘다. 전체(기록 16일 초과)일 때만 가로 스크롤 + 축 고정.
  const VT_AXIS_W=112;
  const VT_SCROLL=reportRange===0&&valueTrend.dates.length>16;
  // 한 화면에 다 넣어야 하는데 점이 많은 경우(올해 등) — 점 숨기고 x축 라벨 자동 솎아냄
  const VT_DENSE=!VT_SCROLL&&valueTrend.dates.length>16;
  const VT_W=Math.max(640,valueTrend.dates.length*52);
  const vtData=useMemo(()=>({labels:valueTrend.dates.map(d=>VT_SCROLL?d:d.slice(5)),datasets:[{label:"총재고금액(원)",data:valueTrend.values,borderColor:"#1A6B45",backgroundColor:"rgba(26,107,69,0.10)",fill:true,tension:.3,pointRadius:VT_DENSE?0:4,pointHoverRadius:5,pointBackgroundColor:"#1A6B45"}]}),[valueTrend,VT_SCROLL,VT_DENSE]);
  const vtOptions=useMemo(()=>({responsive:true,maintainAspectRatio:false,animation:false,
    plugins:{legend:{display:false},tooltip:{callbacks:{title:ctx=>valueTrend.dates[ctx[0].dataIndex]||"",label:ctx=>" ₩"+Math.round(ctx.raw).toLocaleString("ko-KR")}}},
    interaction:VT_DENSE?{mode:"index",intersect:false}:undefined,
    scales:{x:{ticks:{autoSkip:VT_DENSE,maxTicksLimit:VT_DENSE?14:undefined,maxRotation:VT_SCROLL?60:45,minRotation:45,font:{size:VT_SCROLL?12:VT_DENSE?9:10}}},
      y:{afterFit:s=>{s.width=VT_AXIS_W;},ticks:{callback:v=>"₩"+Math.round(v).toLocaleString("ko-KR")}}}}),[VT_SCROLL,VT_DENSE,valueTrend]);
  const vtAxisOptions=useMemo(()=>({...vtOptions,plugins:{legend:{display:false},tooltip:{enabled:false}}}),[vtOptions]);
  // ABC 분석
  const abcAnalysis=useMemo(()=>{
    const valued=items.filter(it=>it.hasCost&&it.value>0).sort((a,b)=>b.value-a.value);
    const total=valued.reduce((s,it)=>s+it.value,0);
    let cum=0;
    return valued.map(it=>{cum+=it.value;return{code:it.code,name:it.name,value:it.value,cumValue:cum,cumPct:total>0?Math.round(cum/total*1000)/10:0};});
  },[items]);
  // ABC 분석: 재고금액 내림차순 + 누적비중 + 등급(A<=70%, B<=90%, C)
  const abcData=useMemo(()=>{
    const arr=items.filter(it=>it.hasCost&&it.value>0).sort((a,b)=>b.value-a.value);
    const total=arr.reduce((s,it)=>s+it.value,0);
    if(!total)return{items:[],total:0,counts:{A:0,B:0,C:0},valuePct:{A:0,B:0,C:0}};
    let cum=0;const out=arr.map(it=>{cum+=it.value;const pct=cum/total*100;const grade=pct<=70?"A":pct<=90?"B":"C";return{code:it.code,name:it.name,value:it.value,cumPct:Math.round(pct*10)/10,grade};});
    const counts={A:0,B:0,C:0},valSum={A:0,B:0,C:0};
    out.forEach(o=>{counts[o.grade]++;valSum[o.grade]+=o.value;});
    const valuePct={A:Math.round(valSum.A/total*100),B:Math.round(valSum.B/total*100),C:Math.round(valSum.C/total*100)};
    return{items:out,total,counts,valuePct};
  },[items]);
  const setOverride=(code,val)=>{const n={...overrides};if(val===""||val==null)delete n[code];else n[code]=val;setOverrides(n);persist({overrides:n});};

  // ===== 리포트 이미지(PNG) 저장 =====
  const exportReportImage=async()=>{
    if(!reportRef.current){flash("리포트 영역을 찾을 수 없습니다.","error");return;}
    try{
      flash("리포트 이미지를 생성하는 중...","info");
      const canvas=await html2canvas(reportRef.current,{backgroundColor:"#ffffff",scale:2,useCORS:true});
      const url=canvas.toDataURL("image/png");
      const a=document.createElement("a");
      a.href=url;
      a.download=`재고리포트_${meta.stockDate?meta.stockDate.slice(0,10):new Date().toISOString().slice(0,10)}.png`;
      a.click();
      flash("리포트 이미지 저장 완료","success");
    }catch(e){flash("이미지 생성 중 문제가 발생했습니다: "+e.message,"error");console.error(e);}
  };

  // ===== 리포트 PDF 저장 (A4 페이지 자동 분할) =====
  const exportReportPDF=async()=>{
    if(!reportRef.current){flash("리포트 영역을 찾을 수 없습니다.","error");return;}
    try{
      flash("리포트 PDF를 생성하는 중...","info");
      const canvas=await html2canvas(reportRef.current,{backgroundColor:"#ffffff",scale:2,useCORS:true});
      const imgW=canvas.width, imgH=canvas.height;
      const pdf=new jsPDF("p","mm","a4");
      const pageW=pdf.internal.pageSize.getWidth();   // 210mm
      const pageH=pdf.internal.pageSize.getHeight();  // 297mm
      const margin=8;
      const usableW=pageW-margin*2;
      // 캔버스 픽셀당 mm 비율
      const pxPerMm=imgW/usableW;
      const pageHpx=Math.floor((pageH-margin*2)*pxPerMm); // 한 페이지에 담을 캔버스 높이(px)
      let rendered=0, pageNo=0;
      while(rendered<imgH){
        const sliceH=Math.min(pageHpx,imgH-rendered);
        const pageCanvas=document.createElement("canvas");
        pageCanvas.width=imgW; pageCanvas.height=sliceH;
        const ctx=pageCanvas.getContext("2d");
        ctx.fillStyle="#ffffff"; ctx.fillRect(0,0,imgW,sliceH);
        ctx.drawImage(canvas,0,rendered,imgW,sliceH,0,0,imgW,sliceH);
        const imgData=pageCanvas.toDataURL("image/png");
        const hMm=sliceH/pxPerMm;
        if(pageNo>0)pdf.addPage();
        pdf.addImage(imgData,"PNG",margin,margin,usableW,hMm);
        rendered+=sliceH; pageNo++;
      }
      const fname=`재고리포트_${meta.stockDate?meta.stockDate.slice(0,10):new Date().toISOString().slice(0,10)}.pdf`;
      pdf.save(fname);
      flash(`리포트 PDF 저장 완료 (${pageNo}페이지)`,"success");
    }catch(e){flash("PDF 생성 중 문제가 발생했습니다: "+e.message,"error");console.error(e);}
  };

  // ===== EXCEL EXPORT =====
  const exportAll=()=>{
    try{
      const wb=XLSXStyle.utils.book_new();
      const allItems=buildItemsFromRaw(rawAdj,overrides,warnDays,overMonths,planDates,dailyMonthly);
      const aDates=planDates.filter(d=>allItems.some(it=>it.inbound&&it.inbound[d]));

      // 색상 팔레트 (헤더용 진한색)
      const COL={sky:"0EA5E9",teal:"0D9488",violet:"7C3AED",fuchsia:"C026D3",rose:"E11D48",orange:"EA580C",amber:"D97706",slate:"64748B",green:"1A6B45",blue:"2563EB"};
      const TINT={sky:"E0F2FE",teal:"CCFBF1",violet:"EDE9FE",fuchsia:"FAE8FF",rose:"FFE4E6",orange:"FFEDD5",amber:"FEF3C7",blue:"DBEAFE"};

      // ── Sheet1: 재고관리 (항목별 색상) ──
      const sorted=[...allItems].sort(byFixedOrder);
      const hdrs1=["상품코드","상품명","월판매량","현재고+발주","가용일수","재고개월수","상태","현재고","발주량",...aDates];
      const data1=sorted.map(it=>{const r={상품코드:it.code,상품명:it.name,월판매량:it.monthly||0,"현재고+발주":it.avail,가용일수:it.days!=null&&isFinite(it.days)?Math.round(it.days*10)/10:"",재고개월수:it.months!=null&&isFinite(it.months)?Math.round(it.months*10)/10:"",상태:it.status,현재고:it.stock,발주량:it.order||0};aDates.forEach(d=>{r[d]=(it.inbound&&it.inbound[d])?it.inbound[d]:"";});return r;});
      const ws1=XLSXStyle.utils.json_to_sheet(data1,{header:hdrs1});
      styleSheet(ws1,[10,38,10,12,10,11,9,10,10,...aDates.map(()=>8)],0,{
        colColors:{2:COL.sky,3:COL.teal,4:COL.violet,5:COL.fuchsia,6:COL.rose,7:COL.orange,8:COL.amber},
        tintColors:{2:TINT.sky,3:TINT.teal,4:TINT.violet,5:TINT.fuchsia,6:TINT.rose,7:TINT.orange,8:TINT.amber}
      });
      XLSXStyle.utils.book_append_sheet(wb,ws1,"재고관리");

      // ── Sheet2: 재고금액 ──
      const vItems=allItems.filter(it=>it.stock!==0).sort((a,b)=>(b.value??-1)-(a.value??-1));
      let total=0;
      const data2=vItems.map(it=>{if(it.hasCost)total+=it.value;return{상품코드:it.code,상품명:it.name,재고량:it.stock,"입고단가(원)":it.unitCost??"","재고금액(원)":it.value??"",비고:it.hasCost?"":"단가 미입력"};});
      data2.push({상품코드:"",상품명:"합계 (단가 적용분)",재고량:"","입고단가(원)":"","재고금액(원)":total,비고:""});
      const ws2=XLSXStyle.utils.json_to_sheet(data2,{header:["상품코드","상품명","재고량","입고단가(원)","재고금액(원)","비고"]});
      styleSheet(ws2,[10,40,12,12,14,14],0,{colColors:{4:COL.green},tintColors:{4:TINT.blue}});
      XLSXStyle.utils.book_append_sheet(wb,ws2,"재고금액");

      // ── Sheet3: 일별판매 (누적/차이) ──
      const sDates=Object.keys(salesHistory).sort();
      if(sDates.length>0){
        const hdrsD=["상품코드","상품명",...sDates.map(d=>d.slice(5)),"기간판매","일평균","변동계수%"];
        const dataD=sorted.map(it=>{
          const row={상품코드:it.code,상품명:it.name};
          const vals=[];
          sDates.forEach(d=>{const v=salesHistoryC[d]&&salesHistoryC[d][it.code];if(typeof v==="number"){row[d.slice(5)]=v;vals.push(v);}else{row[d.slice(5)]="";}});
          const tot=vals.reduce((a,b)=>a+b,0);
          const mean=vals.length?tot/vals.length:null;
          const sd=std(vals);
          row["기간판매"]=vals.length?tot:"";
          row["일평균"]=mean!=null?Math.round(mean):"";
          row["변동계수%"]=(sd!=null&&mean)?Math.round((sd/mean)*1000)/10:"";
          return row;
        });
        const wsD=XLSXStyle.utils.json_to_sheet(dataD,{header:hdrsD});
        styleSheet(wsD,[10,36,...sDates.map(()=>8),11,9,11],0,{colColors:{[hdrsD.length-3]:COL.blue,[hdrsD.length-2]:COL.blue,[hdrsD.length-1]:COL.rose}});
        XLSXStyle.utils.book_append_sheet(wb,wsD,"일별판매");
      }

      // ── Sheet4: 리포트 (요약 먼저 → 상세 나중) ──
      const under=allItems.filter(it=>it.status==="부족").sort((a,b)=>(a.days??1e9)-(b.days??1e9));
      const over=allItems.filter(it=>it.status==="과잉").sort((a,b)=>(b.months??1e9)-(a.months??1e9));
      const normalCnt=allItems.filter(it=>it.status==="정상").length;
      const negCnt=allItems.filter(it=>it.stock<0).length;
      const zeroCnt=allItems.filter(it=>it.stock===0).length;
      const totalQty=allItems.reduce((s,it)=>s+(it.stock||0),0);
      const data3=[];
      // [요약]
      data3.push({구분:"■ 요약",항목:"",값:"",비고:""});
      data3.push({구분:"",항목:"총 재고 금액(단가 적용분)",값:total,비고:"원 (VAT 별도)"});
      data3.push({구분:"",항목:"관리 품목 수",값:allItems.length,비고:"세트·무재고무판매 제외"});
      data3.push({구분:"",항목:"총 재고 수량",값:totalQty,비고:"개"});
      data3.push({구분:"",항목:"정상",값:normalCnt,비고:"건"});
      data3.push({구분:"",항목:"부족",값:under.length-negCnt-zeroCnt,비고:"건 (가용 "+warnDays+"일 미만)"});
      data3.push({구분:"",항목:"재고음수",값:negCnt,비고:"건"});
      data3.push({구분:"",항목:"재고0",값:zeroCnt,비고:"건"});
      data3.push({구분:"",항목:"과잉",값:over.length,비고:"건 (재고 "+overMonths+"개월분 이상)"});
      data3.push({구분:"",항목:"단가 미입력",값:allItems.filter(it=>!it.hasCost&&it.stock!==0).length,비고:"건"});
      data3.push({구분:"",항목:"",값:"",비고:""});
      // [상세 - 부족]
      data3.push({구분:"■ 상세 ① 부족재고",항목:"총 "+under.length+"건 — 발주 우선 검토",값:"",비고:""});
      data3.push({구분:"상품코드",항목:"상품명",값:"현재고/월판매/가용일수",비고:"발주량"});
      under.forEach(it=>data3.push({구분:it.code,항목:it.name,값:`${nf(it.stock)} / ${nf(it.monthly)} / ${it.days!=null?Math.round(it.days*10)/10:"-"}`,비고:it.order||0}));
      data3.push({구분:"",항목:"",값:"",비고:""});
      // [상세 - 과잉]
      data3.push({구분:"■ 상세 ② 과잉재고",항목:"총 "+over.length+"건 — 소진·프로모션 검토",값:"",비고:""});
      data3.push({구분:"상품코드",항목:"상품명",값:"현재고/월판매/재고개월수",비고:"재고금액(원)"});
      over.forEach(it=>data3.push({구분:it.code,항목:it.name,값:`${nf(it.stock)} / ${nf(it.monthly)} / ${it.monthly>0?Math.round(it.months*10)/10:"판매없음"}`,비고:it.value??""}));
      const ws3=XLSXStyle.utils.json_to_sheet(data3,{header:["구분","항목","값","비고"]});
      styleSheet(ws3,[20,40,26,22],1,{colColors:{0:COL.slate,1:COL.slate,2:COL.slate,3:COL.slate}});
      // 요약 제목/총액 강조
      const a1=XLSXStyle.utils.encode_cell({r:0,c:0}); if(ws3[a1])ws3[a1].s={font:{name:"Arial",bold:true,sz:12,color:{rgb:"FFFFFF"}},fill:{patternType:"solid",fgColor:{rgb:COL.green}}};
      const totCell=XLSXStyle.utils.encode_cell({r:1,c:2}); if(ws3[totCell])ws3[totCell].s={font:{name:"Arial",bold:true,sz:11,color:{rgb:COL.green}},border:cellBorder,numFmt:numFmt};
      XLSXStyle.utils.book_append_sheet(wb,ws3,"리포트");

      const fname=`재고관리_${meta.stockDate?meta.stockDate.slice(0,10):new Date().toISOString().slice(0,10)}.xlsx`;
      XLSXStyle.writeFile(wb,fname);
      flash(`${fname} 다운로드 완료 — 구글 드라이브에 올리면 구글 시트로 변환됩니다`,"success");
    }catch(e){flash("내보내기 중 문제가 발생했습니다: "+e.message,"error");console.error(e);}
  };
  // 구매계획(plan/planDates)은 발주 앱에서 자동 반영되는 데이터라 '데이터 비우기' 대상에서 제외한다.
  const clearAll=()=>{
    if(!isAdmin){flash("읽기 전용 계정입니다. 데이터 삭제는 관리자만 가능합니다.","error");return;}
    const b={...raw,stock:null,sales:null,cost:null};
    const clearedMeta={stockDate:"",counts:{plan:meta.counts?.plan},uploadedAt:{plan:meta.uploadedAt?.plan}};
    setRaw(b);setOverrides({});setMeta(clearedMeta);
    persist({raw:b,overrides:{},meta:clearedMeta});
    setShowSettings(false);
    flash("데이터를 비웠습니다. (구매계획은 자동 반영 유지)","info");
  };

  const G="inline-flex items-center gap-1.5 rounded bg-[#0a6ed1] px-3.5 py-2 text-sm font-medium text-white hover:bg-[#0854a0] transition-colors shadow-sm";
  const Ghost="inline-flex items-center gap-1.5 rounded border border-[#d9d9d9] bg-white px-3 py-2 text-sm font-medium text-[#32363a] hover:bg-[#f2f2f2] transition-colors";
  const badge=(s)=>s==="부족"?"bg-red-50 text-red-600 ring-1 ring-red-200":s==="과잉"?"bg-sky-50 text-sky-600 ring-1 ring-sky-200":"bg-slate-50 text-slate-500 ring-1 ring-slate-200";
  const badgeDot=(s)=>s==="부족"?"bg-red-500":s==="과잉"?"bg-sky-500":"bg-slate-400";
  const isTableTab=TABLE_TABS.includes(tab);
  const rowBg=(s)=>s==="부족"?"bg-red-50/60 hover:bg-red-50":s==="과잉"?"bg-sky-50/60 hover:bg-sky-50":"bg-white hover:bg-slate-50";
  const Th=({children,orange,right,sticky,bg})=>(
    <th className={"whitespace-nowrap px-3 py-3 text-xs font-semibold tracking-wide text-slate-200 sticky top-0 z-10 "+(right?"text-right ":children==="상품명"?"text-center ":"text-left ")+(bg?bg:(orange?"bg-orange-500":"bg-slate-800"))+(sticky?" left-0 z-20":"")}>{children}</th>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFile} className="hidden"/>

      {/* TOP NAV */}
      <div className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 py-4 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-white font-bold text-base shadow-sm">B</div>
            <div className="flex items-baseline gap-2.5">
              <span className="text-lg font-semibold tracking-tight text-slate-900">재고·구매 관리</span>
              <span className="text-[11px] text-slate-400 font-medium uppercase tracking-widest">Brandino · Inner Home</span>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {isAdmin&&hasAny&&<button onClick={syncToCloud} disabled={syncing} className="inline-flex items-center gap-1.5 rounded bg-[#0a6ed1] px-3.5 py-2 text-sm font-medium text-white hover:bg-[#0854a0] transition-colors shadow-sm disabled:opacity-50"><Cloud className="h-4 w-4"/>클라우드 저장</button>}
            {hasAny&&<button onClick={exportAll} className="inline-flex items-center gap-1.5 rounded border border-[#d9d9d9] bg-white px-3.5 py-2 text-sm font-medium text-[#32363a] hover:bg-[#f2f2f2] transition-colors"><FileSpreadsheet className="h-4 w-4 text-slate-400"/>구글시트 내보내기</button>}
            <button onClick={()=>setShowSettings(true)} className={Ghost}><Settings className="h-4 w-4"/>설정</button>
            {authReady&&(user?(
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 max-w-[140px] truncate">{user.isAnonymous?"익명 사용자":(user.displayName||user.email||"로그인됨")}</span>
                <span className={"rounded-md text-[11px] font-semibold px-2 py-1 border "+(isAdmin?"bg-indigo-50 text-indigo-600 border-indigo-100":"bg-slate-50 text-slate-400 border-slate-200")}>{isAdmin?"관리자":"읽기 전용"}</span>
                <button onClick={handleLogout} className="inline-flex items-center gap-1 rounded border border-[#d9d9d9] px-2.5 py-2 text-xs font-medium text-[#32363a] hover:bg-[#f2f2f2]"><LogOut className="h-3.5 w-3.5"/>로그아웃</button>
              </div>
            ):(
              <button onClick={handleGoogleLogin} className="inline-flex items-center gap-1.5 rounded bg-[#0a6ed1] px-3 py-2 text-sm font-medium text-white hover:bg-[#0854a0] shadow-sm"><LogIn className="h-4 w-4"/>로그인</button>
            ))}
            <span className="rounded-md bg-slate-50 text-slate-500 text-xs font-medium tabular-nums px-3 py-1.5 border border-slate-200">{meta.stockDate?meta.stockDate.slice(0,10).replace(/-/g,".")+" 기준":"기준일 —"}</span>
          </div>
        </div>
      </div>

      {/* 오늘의 환율 (마켓 티커 바) */}
      <div className="bg-slate-900">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 py-2 flex items-center gap-x-6 gap-y-1 flex-wrap text-xs">
          <span className="flex items-center gap-1.5 font-semibold text-slate-300 tracking-wide">
            <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping"/><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400"/></span>
            오늘의 환율
          </span>
          {fx?(
            <>
              {[["USD",fx.USD],["JPY 100",fx.JPY100],["EUR",fx.EUR],["CNY",fx.CNY]].map(([l,v])=>(
                <span key={l} className="flex items-baseline gap-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{l}</span>
                  <span className="font-semibold tabular-nums text-slate-100">{v.toLocaleString("ko-KR",{maximumFractionDigits:2})}</span>
                  <span className="text-[10px] text-slate-500">KRW</span>
                </span>
              ))}
              <span className="text-[10px] text-slate-500 ml-auto tabular-nums">{fx.at} 기준 · ExchangeRate-API</span>
            </>
          ):(
            <span className="text-slate-500">{fxErr||"불러오는 중…"}</span>
          )}
        </div>
      </div>

      <div className={"mx-auto max-w-[1440px] px-4 sm:px-6 pt-7 "+(isTableTab?"pb-0":"pb-7")}>
        {/* UPLOAD CARDS */}
        <div className="text-[11px] font-semibold text-slate-400 mb-2 uppercase tracking-wider">데이터 파일 업로드</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-4">
          {/* 일별 판매 기록이 있으면 월별 판매(sales) 업로드는 숨김 — 최근 30일 합계가 월판매량을 대체 */}
          {["stock",...(dailyMonthly?[]:["sales"]),"salesDaily"].map(type=>{
            const n=meta.counts?.[type],on=n!=null,m=SLOT_META[type],at=meta.uploadedAt?.[type];
            return(
              <div key={type} onClick={()=>{if(!isAdmin){flash("읽기 전용 계정입니다. 업로드는 관리자만 가능합니다.","error");return;}pendingSlot.current=type;fileRef.current.click();}}
                className="group relative flex overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm cursor-pointer transition-all hover:shadow-md hover:border-slate-300">
                <div className="flex-1 p-2">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <span className={"h-2 w-2 rounded-full "+m.dot}/>
                      <span className="text-xs font-bold text-slate-800">{TYPE_LABEL[type]}</span>
                      {on&&<Check className={"h-3 w-3 "+m.cyc}/>}
                    </div>
                    <span className="text-[10px] text-slate-400 font-medium">{m.fmt}</span>
                  </div>
                  <div className={"rounded-lg border border-dashed py-2 px-2 text-center transition-colors "+(on?"border-slate-200 bg-slate-50/60":"border-slate-200 group-hover:border-slate-300 group-hover:bg-slate-50")}>
                    {on?(
                      <>
                        <div className="flex justify-center mb-1"><Check className={"h-4 w-4 "+m.cyc}/></div>
                        <div className="text-xs font-semibold text-slate-700">{n.toLocaleString("ko-KR")}건 불러옴</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">{m.cycle}{at?` · ${at}`:""}</div>
                      </>
                    ):(
                      <>
                        <div className="text-base mb-0.5">📁</div>
                        <div className="text-xs font-medium text-slate-600">클릭 또는 끌어다 놓기</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">{m.dropSub}</div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <div className="group relative flex overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex-1 p-2">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-teal-500"/>
                  <span className="text-xs font-bold text-slate-800">구매계획</span>
                  {!planSyncErr&&meta.counts?.plan!=null&&<Check className="h-3 w-3 text-teal-600"/>}
                </div>
                <span className="text-[10px] text-slate-400 font-medium">자동</span>
              </div>
              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/60 py-2 px-2 text-center">
                {planSyncErr?(
                  <>
                    <div className="text-xs font-semibold text-red-600">동기화 실패</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{planSyncErr}</div>
                  </>
                ):meta.counts?.plan!=null?(
                  <>
                    <div className="flex justify-center mb-1"><Check className="h-4 w-4 text-teal-600"/></div>
                    <div className="text-xs font-semibold text-slate-700">{meta.counts.plan.toLocaleString("ko-KR")}개 품목 반영</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">구매계획 이력 자동 연동{planSyncedAt?` · ${planSyncedAt}`:""}</div>
                  </>
                ):(
                  <>
                    <div className="text-base mb-0.5">🔄</div>
                    <div className="text-xs font-medium text-slate-600">불러오는 중…</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">구매계획(입고 예정) 이력 동기화</div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="group relative flex overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex-1 p-2">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-amber-500"/>
                  <span className="text-xs font-bold text-slate-800">단가마스터</span>
                  {!costSyncErr&&meta.counts?.cost!=null&&<Check className="h-3 w-3 text-amber-600"/>}
                </div>
                <span className="text-[10px] text-slate-400 font-medium">자동</span>
              </div>
              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/60 py-2 px-2 text-center">
                {costSyncErr?(
                  <>
                    <div className="text-xs font-semibold text-red-600">동기화 실패</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{costSyncErr}</div>
                  </>
                ):meta.counts?.cost!=null?(
                  <>
                    <div className="flex justify-center mb-1"><Check className="h-4 w-4 text-amber-600"/></div>
                    <div className="text-xs font-semibold text-slate-700">{meta.counts.cost.toLocaleString("ko-KR")}개 품목 반영</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">단가마스터 앱 자동 연동{costSyncedAt?` · ${costSyncedAt}`:""}</div>
                  </>
                ):(
                  <>
                    <div className="text-base mb-0.5">🔄</div>
                    <div className="text-xs font-medium text-slate-600">불러오는 중…</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">단가마스터 단가 현황 동기화</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {!hasAny&&loaded&&(
          <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white px-8 py-20 text-center shadow-sm">
            <div className="flex justify-center mb-4"><div className="h-14 w-14 rounded-2xl bg-emerald-50 flex items-center justify-center"><Package className="h-7 w-7 text-emerald-600"/></div></div>
            <p className="text-base font-semibold text-slate-700 mb-1">ERP 파일을 올려 시작하세요</p>
            <p className="text-sm text-slate-400">현재고조회 · 어드민상품매출통계 · 구매계획<br/>상품코드로 자동 결합됩니다 (단가는 단가마스터 앱 연동)</p>
          </div>
        )}

        {hasAny&&(
          <>
            {/* STATS */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              {[
                {label:"총 재고 금액",val:won(totals.value),color:"text-slate-900",icon:<Coins className="h-4 w-4 text-slate-400"/>},
                {label:"총 재고 수량",val:nf(totals.stockT),color:"text-slate-900",icon:<Package className="h-4 w-4 text-slate-400"/>},
                {label:"부족재고",val:nf(totals.low),color:totals.low?"text-red-600":"text-slate-900",icon:<AlertTriangle className={"h-4 w-4 "+(totals.low?"text-red-500":"text-slate-400")}/>},
                {label:"과잉재고",val:nf(totals.over),color:totals.over?"text-sky-600":"text-slate-900",icon:<BarChart3 className={"h-4 w-4 "+(totals.over?"text-sky-500":"text-slate-400")}/>},
              ].map((s,i)=>(
                <div key={i} className="rounded-xl bg-white border border-slate-200 shadow-sm px-5 py-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{s.label}</span>
                    {s.icon}
                  </div>
                  <div className={"text-2xl font-semibold tracking-tight tabular-nums "+s.color}>{s.val}</div>
                </div>
              ))}
            </div>

            {/* 월말 스냅샷 누락 알림 — 기말재고가 없으면 그 달 매출원가를 계산할 수 없다 */}
            {isAdmin&&missingMonthEnds.length>0&&(
              <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-2.5">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0"/>
                <div className="text-xs text-amber-900 leading-relaxed">
                  <span className="font-semibold">월말 재고 기록이 없는 달: {missingMonthEnds.join(", ")}</span>
                  <div className="text-amber-700 mt-0.5">
                    해당 월은 매출매입 화면의 '재고·원가' 탭에서 매출원가를 계산할 수 없습니다.
                    {meta.stockDate?` 지금 화면의 현재고 기준일은 ${meta.stockDate.slice(0,10)} 입니다 — 과거 월의 재고를 지금 값으로 기록하면 원가가 왜곡되니, 해당 월말자 현재고 파일을 올린 뒤 기록해 주세요.`:""}
                  </div>
                </div>
              </div>
            )}

            {/* TABS + 오른쪽 업데이트 공지 게시판 */}
            <div className="flex flex-col lg:flex-row lg:items-stretch gap-4 mb-3">
            <div className="min-w-0 flex-1">
            {/* 메뉴 배치 (2026-07-25 경영자 관점으로 재배치):
                1행 = 경영 판단(주간리포트·매출매입·발주 산출), 2행 = 현황 조회 탭, 3행 = 참고·외부 도구 */}
            {/* 1행 · 경영 판단 */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <button
                onClick={()=>setShowWeeklyReport(true)}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded text-sm font-semibold border border-[#0a6ed1] bg-[#e6f2fb] text-[#0854a0] hover:bg-[#d3e9f9] shadow-sm transition-all whitespace-nowrap"
                title="주간 경영관리 리포트 보기"
              >
                <BarChart3 className="h-4 w-4 text-[#0854a0]"/>주간리포트
              </button>
              <button
                onClick={()=>setShowVendorSales(true)}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded text-sm font-semibold border border-[#0a6ed1] bg-[#e6f2fb] text-[#0854a0] hover:bg-[#d3e9f9] shadow-sm transition-all whitespace-nowrap"
                title="매출·매입 내역과 현금흐름 조회/관리"
              >
                <TrendingUp className="h-4 w-4 text-[#0854a0]"/>매출매입
              </button>
              <button
                onClick={()=>setTab("reorder")}
                className={"flex items-center gap-1.5 px-3.5 py-2 rounded text-sm font-semibold shadow-sm transition-all whitespace-nowrap "+(tab==="reorder"?"bg-[#0a6ed1] text-white border border-[#0a6ed1]":"border border-[#0a6ed1] bg-[#e6f2fb] text-[#0854a0] hover:bg-[#d3e9f9]")}
                title="발주 대상·수량 산출"
              >
                <ShoppingCart className={"h-4 w-4 "+(tab==="reorder"?"text-white":"text-[#0854a0]")}/>발주 산출
              </button>
              <button
                onClick={handleOpenBrandino}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded text-sm font-semibold border border-[#0a6ed1] bg-[#e6f2fb] text-[#0854a0] hover:bg-[#d3e9f9] shadow-sm transition-all whitespace-nowrap"
                title="브랜디노 비서(매출·재고 대시보드) 열기 — 로그인된 구글 계정으로 자동 연결"
              >
                <Bot className="h-4 w-4 text-[#0854a0]"/>브랜디노 비서<ExternalLink className="h-3.5 w-3.5 text-[#0854a0]"/>
              </button>
            </div>

            {/* 2행 · 현황 조회 탭 (배경을 진하게 해서 '탭 그룹'임을 명확히) */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <div className="flex bg-slate-200/70 border border-slate-300 rounded-lg p-1 gap-0.5">
                {[{k:"manage",I:Package,t:"재고관리"},{k:"value",I:Coins,t:"재고금액"},{k:"report",I:BarChart3,t:"리포트"},{k:"daily",I:CalendarClock,t:"일일재고기록"},{k:"sales",I:TrendingUp,t:"일일판매기록"}].map(({k,I,t})=>(
                  <button key={k} onClick={()=>setTab(k)} className={"flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-sm font-medium transition-all "+(tab===k?"bg-white text-[#0854a0] shadow-sm border border-slate-300":"text-slate-600 hover:text-slate-900 hover:bg-slate-100/70")}><I className="h-4 w-4"/>{t}</button>
                ))}
              </div>
            </div>

            {/* 3행 · 참고 · 외부 도구 */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <button
                onClick={()=>setShowHistory(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all whitespace-nowrap"
                title="구매계획 이력 보기"
              >
                <History className="h-3.5 w-3.5 text-teal-600"/>구매계획 이력
              </button>
              <button
                onClick={()=>setShowPaymentDue(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all whitespace-nowrap"
                title="발주 계획 앱의 지급기일·자금소요 조회 (읽기 전용)"
              >
                <CalendarClock className="h-3.5 w-3.5 text-rose-600"/>지급기일 관제
              </button>
              <button
                onClick={()=>setShowNaverInsight(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all whitespace-nowrap"
                title="네이버 쇼핑 트렌드 조회 (데이터랩)"
              >
                <Search className="h-3.5 w-3.5 text-emerald-600"/>쇼핑 인사이트
              </button>
              <a
                href="https://price-manager-49b39.web.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all whitespace-nowrap"
                title="단가마스터 앱 열기 (새 탭)"
              >
                <Tag className="h-3.5 w-3.5 text-amber-600"/>단가마스터/견적산출<ExternalLink className="h-3 w-3 text-slate-400"/>
              </a>
              {SHOW_MARKET_PRICE && (
              <a
                href={`https://market-price-app-f8509.web.app${search ? `?product=${encodeURIComponent(search)}` : ""}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all whitespace-nowrap"
                title="시장가격 조사 웹앱 열기 (새 탭)"
              >
                <ExternalLink className="h-3.5 w-3.5 text-blue-600"/>시장가격 조사
              </a>
              )}
              {showHistory && <PurchasePlanHistoryView onClose={()=>setShowHistory(false)} />}
              {showPaymentDue && <PaymentDueView onClose={()=>setShowPaymentDue(false)} liveFx={fx&&fx.USD?fx.USD:0} fxAt={fx&&fx.at?fx.at:""} />}
              {showVendorSales && <SalesPurchaseView onClose={()=>setShowVendorSales(false)} isAdmin={isAdmin} otherInflows={otherInflows} addOtherInflow={addOtherInflow} removeOtherInflow={removeOtherInflow} valueHistory={valueHistory} brandinoSales={brandinoSales} removeBrandinoSale={removeBrandinoSale} />}
              {showNaverInsight && <NaverInsightView onClose={()=>setShowNaverInsight(false)} />}
              {showSimulation && <PurchaseSimulationView onClose={()=>setShowSimulation(false)} rows={simulationRows} cfg={reorderCfg} />}
              {showSimulation2 && <PurchaseSimulation2View onClose={()=>setShowSimulation2(false)} items={items} reorderRows={reorderRows} rawStock={raw.stock||[]} leadTimes={leadTimes} defaultLT={reorderCfg.defaultLT} canon={canonCode} isAdmin={isAdmin} />}
              {showWeeklyReport && <WeeklyReportView onClose={()=>setShowWeeklyReport(false)} items={items} dailySalesByCode={dailySalesByCode} reorderRows={reorderRows} leadTimes={leadTimes} valueHistory={valueHistory} totals={totals} statusCounts={statusCounts} otherInflows={otherInflows} />}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"/>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="상품코드·상품명 검색" className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-300"/>
              </div>
              {tab==="daily"&&<button onClick={takeSnapshot} className={G}><Camera className="h-4 w-4"/>오늘 재고 기록</button>}
            </div>
            </div>
            <div className="w-full lg:w-[480px] shrink-0 relative">
              <div className="lg:absolute lg:inset-0">
                <UpdateNoticeBoard/>
              </div>
            </div>
            </div>

            {/* TABLE WRAPPER — 표 탭은 화면 위에 닿으면 그대로 붙어 있고(sticky), 표 안에서만 세로 스크롤 */}
            <div className={"rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden "+(isTableTab?"sticky top-0 z-20 flex flex-col":"")} style={isTableTab?{height:TABLE_BOX_H}:undefined}>

              {/* 재고관리: 좌측 고정 컬럼 + 날짜(입고예정) 부분만 가로 스크롤 */}
              {tab==="manage"&&(
                <ScrollXBoth fit>
                <div className="flex items-start w-max min-w-full">
                  {/* 고정 영역: 상품코드 ~ 발주량 (스크롤 시 좌측 고정) */}
                  <table className="border-collapse text-xs table-fixed shrink-0 sticky left-0 z-40 bg-white shadow-[6px_0_8px_-4px_rgba(15,23,42,0.18)]">
                    <colgroup>
                      <col className="w-[64px]"/>{/* 상품코드 */}
                      <col className="w-[230px]"/>{/* 상품명 */}
                      <col className="w-[64px]"/>{/* 월판매량 */}
                      <col className="w-[76px]"/>{/* 현재고+발주 */}
                      <col className="w-[70px]"/>{/* 재고개월수 */}
                      <col className="w-[64px]"/>{/* 상태 */}
                      <col className="w-[60px]"/>{/* 현재고 */}
                      <col className="w-[56px]"/>{/* 발주량 */}
                    </colgroup>
                    <thead><tr className="h-9">
                      <th className="align-middle sticky top-0 z-30 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-left bg-slate-800">상품코드</th>
                      <th className="align-middle sticky top-0 z-30 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-center bg-slate-800">상품명</th>
                      <th className="align-middle sticky top-0 z-30 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-right bg-sky-800">월판매량</th>
                      <th className="align-middle sticky top-0 z-30 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-right bg-teal-800">현재고+발주</th>
                      <th className="align-middle sticky top-0 z-30 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-right bg-indigo-800">재고개월수</th>
                      <th className="align-middle sticky top-0 z-30 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-left bg-rose-800">상태</th>
                      <th className="align-middle sticky top-0 z-30 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-right bg-amber-700">현재고</th>
                      <th className="align-middle sticky top-0 z-30 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-right bg-emerald-800">발주량</th>
                    </tr></thead>
                    <tbody>
                      {manageRows.map(it=>(
                        <tr key={it.code} className={"h-9 border-b border-slate-100 transition-colors "+rowBg(it.status)}>
                          <td className="align-middle px-1.5 py-1.5 font-mono text-[11px] text-slate-400 whitespace-nowrap truncate">{it.code}</td>
                          <td className="align-middle text-left px-1.5 py-1.5 font-medium text-slate-800 whitespace-nowrap truncate" title={it.name}>{it.name}</td>
                          <td className="align-middle px-1.5 py-1.5 text-right tabular-nums text-slate-600 whitespace-nowrap">{nf(it.monthly)}</td>
                          <td className="align-middle px-1.5 py-1.5 text-right tabular-nums font-semibold text-slate-800 whitespace-nowrap">{nf(it.avail)}</td>
                          <td className="align-middle px-1.5 py-1.5 text-right tabular-nums text-slate-500 whitespace-nowrap">{dc(it.months)}</td>
                          <td className="align-middle px-1.5 py-1.5 whitespace-nowrap"><span className={"inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold "+badge(it.status)}><span className={"h-1.5 w-1.5 rounded-full "+badgeDot(it.status)}/>{it.status}</span></td>
                          <td className="align-middle px-1.5 py-1.5 text-right tabular-nums font-bold text-slate-900 whitespace-nowrap">{nf(it.stock)}</td>
                          <td className="align-middle px-1.5 py-1.5 text-right tabular-nums text-slate-600 whitespace-nowrap">{it.order?nf(it.order):<span className="text-slate-300">—</span>}</td>
                        </tr>
                      ))}
                      {manageRows.length===0&&<tr className="h-9"><td colSpan={8} className="py-12 text-center text-xs text-slate-400">표시할 품목이 없습니다</td></tr>}
                    </tbody>
                  </table>
                  {/* 입고예정 날짜 컬럼 영역 */}
                  <div className="grow border-l border-slate-200">
                    <table className="w-full border-collapse text-xs">
                      <thead><tr className="h-9">
                        {adates.map(d=><th key={d} className="align-middle sticky top-0 z-20 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-right bg-slate-600 min-w-[56px]">{d.slice(5)}</th>)}
                        {adates.length===0&&<th className="align-middle sticky top-0 z-20 px-1.5 py-2 text-[11px] font-bold text-white text-left bg-slate-600">입고예정</th>}
                      </tr></thead>
                      <tbody>
                        {manageRows.map(it=>(
                          <tr key={it.code} className={"h-9 border-b border-slate-100 transition-colors "+rowBg(it.status)}>
                            {adates.map(d=><td key={d} className="align-middle px-1.5 py-1.5 text-right tabular-nums text-slate-400 whitespace-nowrap">{it.inbound&&it.inbound[d]?nf(it.inbound[d]):""}</td>)}
                            {adates.length===0&&<td className="align-middle px-1.5 py-1.5"/>}
                          </tr>
                        ))}
                        {manageRows.length===0&&<tr className="h-9"><td colSpan={Math.max(1,adates.length)} className="py-12"/></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
                </ScrollXBoth>
              )}

              {/* 재고금액 */}
              {tab==="value"&&(
                <FitScroll fit className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead><tr><Th>상품코드</Th><Th>상품명</Th><Th right>재고량</Th><Th right>입고단가</Th><Th right>재고금액</Th><Th>비고</Th></tr></thead>
                    <tbody>
                      {valueRows.map(it=>(
                        <tr key={it.code} className={"border-b border-slate-100 transition-colors "+(!it.hasCost&&it.stock!==0?"bg-amber-50 hover:bg-amber-100":"hover:bg-slate-50")}>
                          <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{it.code}</td>
                          <td className="px-3 py-2.5 font-medium text-slate-800">{it.name}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{nf(it.stock)}</td>
                          <td className="px-3 py-2.5 text-right">
                            <input type="number" value={overrides[it.code]!=null?overrides[it.code]:((raw.cost||{})[it.code]!=null?(raw.cost||{})[it.code]:"")} onChange={e=>setOverride(it.code,e.target.value)} placeholder="미입력"
                              className={"w-24 rounded-lg border px-2 py-1 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-300 "+(it.hasCost?"border-slate-200 bg-white":"border-amber-300 bg-amber-50")}/>
                          </td>
                          <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{won(it.value)}</td>
                          <td className="px-3 py-2.5">{!it.hasCost&&it.stock!==0&&<span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full"><AlertTriangle className="h-3 w-3"/>단가 미입력</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-slate-200 bg-slate-50">
                        <td className="px-3 py-3 font-bold text-slate-700" colSpan={4}>합계 (단가 적용분)</td>
                        <td className="px-3 py-3 text-right font-bold text-emerald-700 tabular-nums text-base">{won(totals.value)}</td>
                        <td className="px-3 py-3 text-xs text-amber-600">{totals.missing?`미입력 ${nf(totals.missing)}개 제외`:""}</td>
                      </tr>
                    </tfoot>
                  </table>
                </FitScroll>
              )}

              {/* 일일재고기록 */}
              {tab==="daily"&&(
                <>
                  <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 text-xs text-slate-500">
                    기록일 {histDates.length}일 · '오늘 재고 기록' 버튼으로 스냅샷을 누적하세요 · 변동계수(CV%)가 클수록 변동성이 큰 품목
                  </div>
                  <ScrollXBoth fit>
                    <table className="w-full border-collapse text-sm">
                      <thead><tr>
                        <th className="whitespace-nowrap px-3 py-2.5 text-xs font-bold tracking-wide text-white text-left bg-slate-800 sticky left-0 top-0 z-30 min-w-[84px] max-w-[84px] w-[84px]">상품코드</th>
                        <th className="whitespace-nowrap px-3 py-2.5 text-xs font-bold tracking-wide text-white text-center bg-slate-800 sticky left-[84px] top-0 z-30 min-w-[280px] max-w-[280px] w-[280px]">상품명</th>
                        {histDates.map(d=><Th key={d} right>{d.slice(5)}</Th>)}
                        <Th right>기록일수</Th><Th right>평균</Th><Th right>표준편차</Th><Th right>변동폭</Th><Th right>변동계수%</Th>
                      </tr></thead>
                      <tbody>
                        {volRows.filter(v=>match({code:v.code,name:v.name})).sort((a,b)=>(b.cv??-1)-(a.cv??-1)).map(v=>(
                          <tr key={v.code} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-3 py-2.5 font-mono text-xs text-slate-500 sticky left-0 z-10 bg-white min-w-[84px] max-w-[84px] w-[84px]">{v.code}</td>
                            <td className="align-middle px-2 py-2.5 text-[11px] font-medium text-slate-800 sticky left-[84px] z-10 bg-white min-w-[280px] max-w-[280px] w-[280px]" title={v.name}><div className="whitespace-normal break-words leading-snug">{v.name}</div></td>
                            {histDates.map(d=><td key={d} className="px-3 py-2.5 text-right tabular-nums text-slate-500">{history[d]&&typeof history[d][v.code]==="number"?nf(history[d][v.code]):""}</td>)}
                            <td className="px-3 py-2.5 text-right tabular-nums">{v.n}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{v.mean==null?"—":nf(Math.round(v.mean))}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{v.sd==null?"—":dc(v.sd)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{v.range==null?"—":nf(v.range)}</td>
                            <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{v.cv==null?"—":dc(v.cv)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollXBoth>
                  {histDates.length===0&&<div className="py-12 text-center text-sm text-slate-400">아직 기록이 없습니다. '오늘 재고 기록'을 눌러 첫 스냅샷을 저장하세요.</div>}
                </>
              )}

              {/* 일일판매기록 */}
              {tab==="sales"&&(
                <>
                  <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 text-xs text-slate-500">
                    기록일 {salesDates.length}일 · 상단 '일별 판매' 칸에 매일 파일(파일명에 날짜 포함)을 올리면 그날 판매수량으로 자동 누적됩니다 · 재고관리의 월판매량은 최근 30일 합계로 자동 계산(30일 미만 기록 시 일평균×30 환산) · 변동계수(CV%)가 클수록 판매 변동이 큰 품목
                  </div>
                  {Object.keys(SALES_ADJUSTMENTS).length>0&&(
                    <div className={"px-4 py-2 border-b text-xs "+(salesAdjustMissing.length?"bg-amber-50 border-amber-200 text-amber-800":"bg-sky-50 border-sky-100 text-sky-800")}>
                      <span className="font-semibold">수기 판매 보정</span>{" · "}
                      {Object.keys(SALES_ADJUSTMENTS).sort().map(d=>{
                        const missing=salesAdjustMissing.includes(d);
                        return Object.keys(SALES_ADJUSTMENTS[d]).map(code=>(
                          <span key={d+code} className="mr-3">
                            {d} {code} {SALES_ADJUSTMENTS[d][code]>0?"+":""}{nf(SALES_ADJUSTMENTS[d][code])}개
                            {missing?" (해당일 기록 없음 — 미적용)":" (반영됨)"}
                          </span>
                        ));
                      })}
                      {salesAdjustMissing.length>0&&<span className="block mt-1">보정 대상일의 일별 판매 파일을 먼저 업로드하면 자동으로 합산됩니다.</span>}
                    </div>
                  )}
                  <ScrollXBoth fit>
                    <table className="w-full border-collapse text-sm">
                      <thead><tr>
                        <th className="whitespace-nowrap px-3 py-2.5 text-xs font-bold tracking-wide text-white text-left bg-slate-800 sticky left-0 top-0 z-30 min-w-[84px] max-w-[84px] w-[84px]">상품코드</th>
                        <th className="whitespace-nowrap px-3 py-2.5 text-xs font-bold tracking-wide text-white text-center bg-slate-800 sticky left-[84px] top-0 z-30 min-w-[280px] max-w-[280px] w-[280px]">상품명</th>
                        {salesDates.map(d=><Th key={d} right>{d.slice(5)}</Th>)}
                        <Th right>기간판매</Th><Th right>일평균</Th><Th right>월판매</Th><Th right>최근일</Th><Th right>추세</Th><Th right>변동계수%</Th>
                      </tr></thead>
                      <tbody>
                        {salesVolRows.filter(v=>match({code:v.code,name:v.name})).sort((a,b)=>(b.total??-1)-(a.total??-1)).map(v=>{
                          const ds=dailySalesByCode[v.code]||[];
                          const dmap={};ds.forEach(x=>dmap[x.date]=x.qty);
                          return(
                          <tr key={v.code} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-3 py-2.5 font-mono text-xs text-slate-500 sticky left-0 z-10 bg-white min-w-[84px] max-w-[84px] w-[84px]">{v.code}</td>
                            <td className="align-middle px-2 py-2.5 text-[11px] font-medium text-slate-800 sticky left-[84px] z-10 bg-white min-w-[280px] max-w-[280px] w-[280px]" title={v.name}><div className="whitespace-normal break-words leading-snug">{v.name}</div></td>
                            {salesDates.map(d=><td key={d} className="px-3 py-2.5 text-right tabular-nums text-slate-500">{dmap[d]!=null?nf(dmap[d]):""}</td>)}
                            <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{v.n?nf(v.total):"—"}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{v.mean==null?"—":nf(Math.round(v.mean))}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-sky-700">{v.monthly==null?"—":nf(v.monthly)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums">{v.recent==null?"—":nf(v.recent)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums">{v.trend==null?"—":<span className={v.trend>0?"text-emerald-600 font-medium":v.trend<0?"text-red-500 font-medium":"text-slate-400"}>{v.trend>0?"\u25b2 ":v.trend<0?"\u25bc ":""}{nf(Math.abs(v.trend))}</span>}</td>
                            <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{v.cv==null?"—":dc(v.cv)}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </ScrollXBoth>
                  {salesDates.length===0&&<div className="py-12 text-center text-sm text-slate-400">아직 기록이 없습니다. 상단 '일별 판매' 칸에 파일을 올리면 파일명 날짜로 자동 누적됩니다.</div>}
                </>
              )}

              {/* 발주 산출 */}
              {tab==="reorder"&&(
                <>
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-600">
                    <label className="flex items-center gap-1.5">서비스레벨
                      <select value={reorderCfg.svc} onChange={e=>setCfg({svc:Number(e.target.value)})} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-300">
                        <option value={90}>90% (z=1.28)</option><option value={95}>95% (z=1.65)</option><option value={99}>99% (z=2.33)</option>
                      </select>
                    </label>
                    <label className="flex items-center gap-1.5">목표 재고일수
                      <input type="number" min={1} value={reorderCfg.targetDays} onChange={e=>setCfg({targetDays:Math.max(1,toNum(e.target.value))})} className="w-16 rounded-lg border border-slate-200 bg-white px-2 py-1 text-right text-xs tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-300"/>일
                    </label>
                    <label className="flex items-center gap-1.5">기본 리드타임
                      <input type="number" min={1} value={reorderCfg.defaultLT} onChange={e=>setCfg({defaultLT:Math.max(1,toNum(e.target.value))})} className="w-16 rounded-lg border border-slate-200 bg-white px-2 py-1 text-right text-xs tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-300"/>일
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                      <input type="checkbox" checked={onlyOrder} onChange={e=>setOnlyOrder(e.target.checked)} className="accent-emerald-600"/>발주 대상만 보기
                    </label>
                    <div className="ml-auto flex items-center gap-2">
                      {pickedCodes.length>0&&(
                        <button onClick={()=>setPickedCodes([])} className="text-[11px] font-medium text-slate-400 hover:text-slate-600 underline">선택 해제</button>
                      )}
                      <button onClick={openSimulation} className={"inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors "+(pickedCodes.length>0?"bg-[#0a6ed1] text-white hover:bg-[#0854a0]":"bg-slate-100 text-slate-400 border border-slate-200")}>
                        <SlidersHorizontal className="h-3.5 w-3.5"/>구매 시뮬레이션 ①{pickedCodes.length>0?` (${nf(pickedCodes.length)})`:""}
                      </button>
                      <button onClick={()=>setShowSimulation2(true)} className="inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors bg-indigo-600 text-white hover:bg-indigo-700" title="카테고리별 2개월 수급 시뮬레이션 후 엑셀 내보내기">
                        <SlidersHorizontal className="h-3.5 w-3.5"/>구매 시뮬레이션 ②
                      </button>
                      <button onClick={exportReorderPicked} className={"inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors "+(pickedCodes.length>0?"bg-[#107e3e] text-white hover:bg-[#0b5c2d]":"bg-slate-100 text-slate-400 border border-slate-200")}>
                        <FileSpreadsheet className="h-3.5 w-3.5"/>선택 대상 엑셀{pickedCodes.length>0?` (${nf(pickedCodes.length)})`:""}
                      </button>
                      <button onClick={exportReorder} className="inline-flex items-center gap-1.5 rounded bg-[#e9730c] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#c15c0a] transition-colors">
                        <FileSpreadsheet className="h-3.5 w-3.5"/>발주 대상 엑셀
                      </button>
                    </div>
                  </div>
                  <div className="px-4 py-2.5 bg-white border-b border-slate-100 flex flex-wrap items-center gap-4 text-xs">
                    <span className="font-semibold text-red-600">🔴 지금발주 {nf(reorderRows.filter(r=>r.signal==="지금발주").length)}</span>
                    <span className="font-semibold text-amber-600">🟡 곧발주 {nf(reorderRows.filter(r=>r.signal==="곧발주").length)}</span>
                    <span className="font-semibold text-emerald-600">🟢 여유 {nf(reorderRows.filter(r=>r.signal==="여유").length)}</span>
                    {pickedCodes.length>0&&<span className="font-semibold text-emerald-700">☑ 선택 {nf(pickedCodes.length)}</span>}
                    <span className="text-slate-400">· 수식: 권장발주량 = (일평균×목표일수 + 안전재고) + 일평균×리드타임 − (현재고+발주잔량) · 안전재고 = z×σ×√LT (Y등급 z+0.5, Z등급은 수동 검토 권장) · 월평균 = 일평균×30 (월 환산값) · 현재고 0 이하이고 판매량 0인 품목만 일평균판매 칸에 가정값을 입력해 시뮬레이션 가능 (실제 판매 기록 시 자동 전환) · 왼쪽 체크박스로 고른 품목은 [선택 대상 엑셀]로 따로 받을 수 있습니다(실제발주량 칸이 빈 칸으로 함께 나갑니다)</span>
                  </div>
                  {/* 가로 스크롤 없이 전체 컬럼이 한 화면에 보이도록 컴팩트 레이아웃 · 세로는 표 안에서만 스크롤(헤더 고정) */}
                  <FitScroll fit>
                  <table className="w-full border-collapse text-xs table-fixed">
                    <colgroup>
                      <col className="w-[30px]"/>{/* 선택 */}
                      <col className="w-[58px]"/>{/* 코드 */}
                      <col/>{/* 상품명 (남는 폭 전부) */}
                      <col className="w-[70px]"/>{/* 일평균 */}
                      <col className="w-[56px]"/>{/* 월평균 */}
                      <col className="w-[44px]"/>{/* CV */}
                      <col className="w-[44px]"/>{/* 등급 */}
                      <col className="w-[56px]"/>{/* LT */}
                      <col className="w-[58px]"/>{/* 현재고 */}
                      <col className="w-[52px]"/>{/* 발주잔량 */}
                      <col className="w-[52px]"/>{/* 안전재고 */}
                      <col className="w-[54px]"/>{/* 발주점 */}
                      <col className="w-[48px]"/>{/* 소진 */}
                      <col className="w-[74px]"/>{/* 판정 */}
                      <col className="w-[66px]"/>{/* 발주량 */}
                    </colgroup>
                    <thead><tr>
                      <th className="sticky top-0 z-20 px-1 py-2 text-center bg-slate-800">
                        <input type="checkbox" checked={allVisiblePicked} onChange={toggleAllVisible} title="화면에 보이는 품목 전체 선택/해제" className="accent-emerald-500 cursor-pointer"/>
                      </th>
                      <th className="sticky top-0 z-20 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-left bg-slate-800">코드</th>
                      <th className="sticky top-0 z-20 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-center bg-slate-800">상품명</th>
                      <th className="sticky top-0 z-20 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-right bg-sky-800">일평균</th>
                      <th className="sticky top-0 z-20 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-right bg-sky-800">월평균</th>
                      <th className="sticky top-0 z-20 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-right bg-sky-800">CV</th>
                      <th className="sticky top-0 z-20 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-center bg-sky-800">등급</th>
                      <th className="sticky top-0 z-20 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-right bg-violet-800">LT(일)</th>
                      <th className="sticky top-0 z-20 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-right bg-amber-700">현재고</th>
                      <th className="sticky top-0 z-20 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-right bg-amber-700">발주</th>
                      <th className="sticky top-0 z-20 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-right bg-teal-800">안전</th>
                      <th className="sticky top-0 z-20 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-right bg-teal-800">발주점</th>
                      <th className="sticky top-0 z-20 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-right bg-indigo-800">소진</th>
                      <th className="sticky top-0 z-20 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-center bg-rose-800">판정</th>
                      <th className="sticky top-0 z-20 whitespace-nowrap px-1.5 py-2 text-[11px] font-bold text-white text-right bg-orange-600">발주량</th>
                    </tr></thead>
                    <tbody>
                      {reorderVisible.map(r=>{
                        const sigCls=r.signal==="지금발주"?"bg-red-50 text-red-600 ring-1 ring-red-200":r.signal==="곧발주"?"bg-amber-50 text-amber-700 ring-1 ring-amber-200":"bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200";
                        const sigDot=r.signal==="지금발주"?"bg-red-500":r.signal==="곧발주"?"bg-amber-500":"bg-emerald-500";
                        const picked=pickedCodes.includes(String(r.code));
                        const rowCls=picked?"bg-emerald-50 hover:bg-emerald-100":r.signal==="지금발주"?"bg-red-50/60 hover:bg-red-50":r.signal==="곧발주"?"bg-amber-50/50 hover:bg-amber-50":"bg-white hover:bg-slate-50";
                        return(
                        <tr key={r.code} className={"border-b border-slate-100 transition-colors "+rowCls}>
                          <td className="px-1 py-1.5 text-center">
                            <input type="checkbox" checked={picked} onChange={()=>togglePicked(r.code)} className="accent-emerald-600 cursor-pointer"/>
                          </td>
                          <td className="px-1.5 py-1.5 font-mono text-[11px] text-slate-500">{r.code}</td>
                          <td className="px-1.5 py-1.5 font-medium text-slate-800"><div className="whitespace-normal break-words leading-snug line-clamp-2">{r.name}</div></td>
                          <td className="px-1.5 py-1.5 text-right tabular-nums text-slate-600">
                            {r.editable?(
                              <input type="number" min={0} step="0.1" value={dailyOverrides[r.code]!=null?dailyOverrides[r.code]:""} placeholder={r.dAvg>0?dc(r.dAvg):"가정"} onChange={e=>setDailyOverride(r.code,e.target.value)}
                                title="현재고 0 이하이고 판매량 0인 품목 — 가정 일평균 판매량을 입력하면 적정 구매량을 산출합니다 (실제 판매가 기록되면 자동으로 실측값 사용)"
                                className={"w-full rounded-md border px-1 py-0.5 text-right text-xs tabular-nums focus:outline-none focus:ring-2 focus:ring-cyan-300 "+(r.manual?"border-cyan-400 bg-cyan-50":"border-dashed border-cyan-300 bg-white")}/>
                            ):dc(r.dAvg)}
                          </td>
                          <td className="px-1.5 py-1.5 text-right tabular-nums text-slate-600" title="일평균 × 30 (월 환산)">{r.dAvg>0?nf(Math.round(r.dAvg*30)):<span className="text-slate-300">—</span>}</td>
                          <td className="px-1.5 py-1.5 text-right tabular-nums text-slate-500">{r.cv==null?"—":dc(r.cv,1)}</td>
                          <td className="px-1.5 py-1.5 text-center"><span className={"inline-flex rounded-full px-1.5 py-0.5 text-[11px] font-bold "+(r.manual?"bg-cyan-100 text-cyan-700":r.grade==="X"?"bg-emerald-100 text-emerald-700":r.grade==="Y"?"bg-amber-100 text-amber-700":r.grade==="Z"?"bg-red-100 text-red-700":"bg-slate-100 text-slate-400")}>{r.manual?"수동":r.grade||"—"}</span></td>
                          <td className="px-1.5 py-1.5 text-right">
                            <input type="number" min={1} value={leadTimes[r.code]!=null?leadTimes[r.code]:""} placeholder={String(reorderCfg.defaultLT)} onChange={e=>setLeadTime(r.code,e.target.value)}
                              className="w-full rounded-md border border-slate-200 bg-white px-1 py-0.5 text-right text-xs tabular-nums focus:outline-none focus:ring-2 focus:ring-violet-300"/>
                          </td>
                          <td className="px-1.5 py-1.5 text-right tabular-nums font-semibold text-slate-800">{nf(r.stock)}</td>
                          <td className="px-1.5 py-1.5 text-right tabular-nums text-slate-500"
                              title={r.order?`발주잔량 ${nf(r.order)} — 리드타임(${r.lt}일) 내 납기 ${nf(r.orderIn)} · 그 이후 납기 ${nf(r.orderLater)} (가용 재고에는 리드타임 내 납기분만 반영)`:""}>
                            {r.order?(<span>{nf(r.orderIn)}{r.orderLater>0&&<span className="text-slate-300"> +{nf(r.orderLater)}</span>}</span>):<span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-1.5 py-1.5 text-right tabular-nums text-slate-500">{nf(Math.round(r.ss))}</td>
                          <td className="px-1.5 py-1.5 text-right tabular-nums text-slate-600">{nf(Math.round(r.rop))}</td>
                          <td className="px-1.5 py-1.5 text-right tabular-nums">{r.runout==null?"—":<span className={r.runout<=r.lt?"text-red-600 font-semibold":r.runout<=r.lt*1.5?"text-amber-600 font-medium":"text-slate-500"}>{dc(r.runout,0)}</span>}</td>
                          <td className="px-1 py-1.5 text-center"><span className={"inline-flex items-center whitespace-nowrap gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold "+sigCls}><span className={"h-1.5 w-1.5 rounded-full shrink-0 "+sigDot}/>{r.signal}</span></td>
                          <td className="px-1.5 py-1.5 text-right tabular-nums font-bold text-orange-600">{r.qty>0?nf(r.qty):<span className="text-slate-300">—</span>}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </FitScroll>
                  {salesDates.length===0&&<div className="py-12 text-center text-sm text-slate-400">일별 판매 기록이 필요합니다. 상단 '일별 판매' 칸에 파일을 올린 뒤 이용하세요.</div>}
                </>
              )}

              {/* 리포트 */}
              {tab==="report"&&(
                <div className="p-4 space-y-4">
                  {/* 기간 필터 + 이미지 저장 */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-slate-500 mr-1">기간</span>
                    {REPORT_RANGES.map(o=>(
                      <button key={o.v} onClick={()=>setReportRange(o.v)} className={"px-3 py-1.5 rounded text-xs font-medium transition-colors border "+(reportRange===o.v?"bg-[#0a6ed1] text-white border-[#0a6ed1]":"bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>{o.l}</button>
                    ))}
                    <span className="text-xs text-slate-400 ml-1">{rangeDates.length>0?`(${rangeDates[0]} ~ ${rangeDates[rangeDates.length-1]}, ${rangeDates.length}일)`:"일별 판매 기록 없음"}</span>
                    <div className="ml-auto flex items-center gap-2">
                      <button onClick={exportReportPDF} className="inline-flex items-center gap-1.5 rounded bg-[#107e3e] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0b5c2d]"><FileSpreadsheet className="h-3.5 w-3.5"/>리포트 PDF 저장</button>
                      <button onClick={exportReportImage} className="inline-flex items-center gap-1.5 rounded bg-[#354a5f] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#26374a]"><Download className="h-3.5 w-3.5"/>이미지(PNG)</button>
                    </div>
                  </div>
                  <div ref={reportRef} className="space-y-4 bg-white">
                  {/* 일별 총재고금액 변동 */}
                  <div className="rounded-xl border border-slate-200 shadow-sm p-4">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <div className="text-sm font-bold text-slate-700">일별 총재고금액 변동 <span className="font-normal text-slate-400">({rangeLabel})</span></div>
                      <div className="ml-auto"><RangeChips value={reportRange} onChange={setReportRange}/></div>
                    </div>
                    {valueTrend.dates.length===0?<div className="py-12 text-center text-sm text-slate-400">'오늘 재고 기록'을 누르면 그날의 총재고금액이 저장되어 추이가 그려집니다</div>:
                    <>
                      {VT_SCROLL?(
                      <div className="relative">
                        <div className="overflow-x-auto pb-2">
                          <div style={{minWidth:VT_W+"px"}}>
                            <ChartCanvas height={280} ariaLabel="일별 총재고금액 변동 꺾은선 차트"
                              type="line" data={vtData} options={vtOptions}/>
                          </div>
                        </div>
                        {/* 좌측 금액 축 고정(스크롤해도 항상 보임) */}
                        <div className="absolute left-0 top-0 pointer-events-none bg-white" style={{width:VT_AXIS_W+"px",height:"280px",overflow:"hidden"}}>
                          <div style={{minWidth:VT_W+"px"}}>
                            <ChartCanvas height={280} ariaLabel="" type="line" data={vtData} options={vtAxisOptions}/>
                          </div>
                        </div>
                      </div>
                      ):(
                        <ChartCanvas height={300} ariaLabel="일별 총재고금액 변동 꺾은선 차트"
                          type="line" data={vtData} options={vtOptions}/>
                      )}
                      <div className="text-[11px] text-slate-400 mt-1">표시 {valueTrend.dates.length}일 / 전체 기록 {valueTrend.totalDays}일 · 최근 기록일 {valueTrend.dates[valueTrend.dates.length-1]}{VT_SCROLL?" · 그래프가 넓으면 좌우로 스크롤하세요":" · 한 화면에 모두 표시됩니다"} (기록이 없는 날짜는 '오늘 재고 기록'을 눌러야 채워집니다)</div>
                    </>}
                  </div>
                  {/* 월별 총재고금액 (최근 12개월) */}
                  <div className="rounded-xl border border-slate-200 shadow-sm p-4">
                    <div className="text-sm font-bold text-slate-700 mb-2">월별 총재고금액 (최근 12개월)</div>
                    {monthlyValueTrend.months.length===0?<div className="py-12 text-center text-sm text-slate-400">'오늘 재고 기록'이 쌓이면 월말 기준 재고금액이 월별로 집계됩니다</div>:
                    <>
                      <ChartCanvas height={300} ariaLabel="월별 총재고금액 막대 차트"
                        type="bar"
                        data={{labels:monthlyValueTrend.months,datasets:[{label:"월말 총재고금액(원)",data:monthlyValueTrend.values,backgroundColor:"#1A6B45",borderRadius:4,maxBarThickness:56}]}}
                        options={{responsive:true,maintainAspectRatio:false,plugins:{legend:{display:false},tooltip:{callbacks:{label:ctx=>" ₩"+Math.round(ctx.raw).toLocaleString("ko-KR"),afterLabel:ctx=>"기준일 "+monthlyValueTrend.basis[ctx.dataIndex]}}},scales:{y:{beginAtZero:true,ticks:{callback:v=>"₩"+Math.round(v).toLocaleString("ko-KR")}}}}}/>
                      <div className="text-[11px] text-slate-400 mt-1">각 월의 <b>마지막 기록일</b> 총재고금액 기준입니다 · 기록이 있는 달만 표시됩니다</div>
                    </>}
                  </div>
                  {/* 차트 영역 */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="rounded-xl border border-slate-200 shadow-sm p-4">
                      <div className="text-sm font-bold text-slate-700 mb-1">상태별 품목 구성</div>
                      <div className="flex flex-wrap gap-3 mb-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{background:"#639922"}}/>정상 {statusCounts.정상}</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{background:"#E24B4A"}}/>부족 {statusCounts.부족}</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{background:"#378ADD"}}/>과잉 {statusCounts.과잉}</span>
                      </div>
                      <ChartCanvas height={260} ariaLabel="상태별 품목 구성 도넛 차트"
                        type="doughnut"
                        data={{labels:["정상","부족","과잉"],datasets:[{data:[statusCounts.정상,statusCounts.부족,statusCounts.과잉],backgroundColor:["#639922","#E24B4A","#378ADD"],borderWidth:2,borderColor:"#fff"}]}}
                        options={{responsive:true,maintainAspectRatio:false,cutout:"62%",plugins:{legend:{display:false}}}}/>
                    </div>
                    <div className="rounded-xl border border-slate-200 shadow-sm p-4">
                      <div className="text-sm font-bold text-slate-700 mb-2">재고 금액 상위 10</div>
                      {topValueItems.length===0?<div className="py-16 text-center text-sm text-slate-400">단가가 입력된 품목이 있어야 표시됩니다</div>:
                      <ChartCanvas height={300} ariaLabel="재고 금액 상위 10개 상품 가로 막대 차트"
                        type="bar"
                        data={{labels:topValueItems.map(it=>it.name.length>16?it.name.slice(0,16)+"…":it.name),datasets:[{label:"재고금액(원)",data:topValueItems.map(it=>it.value),backgroundColor:"#185FA5",borderRadius:4}]}}
                        options={{indexAxis:"y",responsive:true,maintainAspectRatio:false,plugins:{legend:{display:false}},scales:{x:{ticks:{callback:v=>"₩"+Math.round(v).toLocaleString("ko-KR")}}}}}/>}
                    </div>
                    <div className="rounded-xl border border-slate-200 shadow-sm p-4 lg:col-span-2">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <div className="text-sm font-bold text-slate-700">
                          일별 판매 추세 {trendPicked.length>0?`— 선택 ${nf(trendPicked.length)}개`:"(판매 상위 10)"}
                        </div>
                        <div className="ml-auto flex items-center gap-2">
                          <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none"/>
                            <input value={trendSearch} onChange={e=>setTrendSearch(e.target.value)} placeholder="상품코드·상품명으로 아이템 검색"
                              className="w-60 pl-8 pr-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"/>
                          </div>
                          {trendSearch&&<button onClick={()=>setTrendSearch("")} className="text-[11px] font-medium text-slate-400 hover:text-slate-600 whitespace-nowrap">검색 지우기</button>}
                          {trendPicked.length>0&&<button onClick={()=>setTrendPicked([])} className="text-[11px] font-medium text-slate-400 hover:text-slate-600 underline whitespace-nowrap">전체 해제</button>}
                        </div>
                      </div>

                      {/* 검색 결과 — 클릭해서 그래프에 추가/제거 (숫자는 선택 기간 내 판매수량) */}
                      {trendSearch.trim()&&(
                        <div className="mb-2 rounded-lg border border-slate-200 bg-slate-50/70 p-2 max-h-32 overflow-y-auto">
                          {trendCandidates.length===0?(
                            <p className="text-[11px] text-slate-400 px-1 py-0.5">검색 결과가 없습니다.</p>
                          ):(
                            <div className="flex flex-wrap gap-1">
                              {trendCandidates.map(v=>{
                                const on=trendPicked.includes(String(v.code));
                                return(
                                  <button key={v.code} onClick={()=>toggleTrendPick(v.code)} title={`${v.code} · ${v.name}`}
                                    className={"inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium border transition-colors whitespace-nowrap "+(on?"bg-emerald-600 text-white border-emerald-600":"bg-white text-slate-600 border-slate-200 hover:border-emerald-300 hover:bg-emerald-50")}>
                                    {on&&<Check className="h-3 w-3 shrink-0"/>}
                                    <span>{v.name||v.code}</span>
                                    <span className={"text-[9px] tabular-nums font-normal "+(on?"text-emerald-200":"text-slate-400")}>{nf(v.total)}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                      {/* 선택된 아이템 — 칩 색이 그래프 선 색과 같습니다 */}
                      {trendPicked.length>0&&(
                        <div className="flex flex-wrap gap-1 mb-2">
                          {trendSeries.map((v,i)=>(
                            <span key={v.code} className="inline-flex items-center gap-1 rounded-full bg-white border px-2 py-0.5 text-[11px] font-medium text-slate-700 whitespace-nowrap" style={{borderColor:TREND_COLORS[i%10]}}>
                              <span className="h-2 w-2 rounded-full shrink-0" style={{background:TREND_COLORS[i%10]}}/>
                              <span title={`${v.code} · ${v.name}`}>{v.name||v.code}</span>
                              <span className="text-[9px] tabular-nums font-normal text-slate-400">{nf(v.total)}</span>
                              <button onClick={()=>toggleTrendPick(v.code)} title="그래프에서 제거" className="ml-0.5 text-slate-400 hover:text-red-600 leading-none">×</button>
                            </span>
                          ))}
                        </div>
                      )}

                      {trendSeries.length===0?(
                        <div className="py-16 text-center text-sm text-slate-400">
                          {trendPicked.length>0?"선택한 품목의 판매 기록이 없습니다":"일별 판매 기록이 쌓이면 표시됩니다"}
                        </div>
                      ):(
                      /* 제품명을 자르지 않으니 범례가 여러 줄이 될 수 있어 품목 수에 따라 높이를 늘린다 */
                      <ChartCanvas height={Math.min(560,330+trendSeries.length*16)} ariaLabel={trendPicked.length>0?`선택한 ${trendSeries.length}개 상품의 일별 판매 추세 꺾은선 차트`:"판매 상위 10개 상품의 일별 판매 추세 꺾은선 차트"}
                        type="line"
                        data={{labels:rangeDates,datasets:trendSeries.map((v,i)=>{const arr=(dailySalesByCode[v.code]||[]);const dmap={};arr.forEach(x=>dmap[x.date]=x.qty);return{label:v.name||v.code,data:rangeDates.map(d=>dmap[d]??null),borderColor:TREND_COLORS[i%10],backgroundColor:TREND_COLORS[i%10],tension:.3,pointStyle:TREND_POINTS[i%10],pointRadius:3,borderDash:i%2===1?[5,3]:[]};})}}
                        options={{responsive:true,maintainAspectRatio:false,plugins:{legend:{display:true,position:"bottom",labels:{boxWidth:12,font:{size:10}}},tooltip:{callbacks:{label:ctx=>` ${ctx.dataset.label}: ${nf(ctx.raw)}`}}},scales:{y:{title:{display:true,text:"판매수량"},ticks:{font:{size:10}}},x:{ticks:{font:{size:10}}}}}}/>
                      )}
                      {trendPicked.length===0&&<p className="text-[11px] text-slate-400 mt-2">검색창에 상품코드나 상품명을 입력한 뒤 결과를 클릭하면, 그 품목만의 일별 판매 추세를 보여줍니다 (최대 {TREND_MAX}개 동시 비교).</p>}
                    </div>
                    {/* 일별 판매 합계 — 일별 판매 추세와 같이 한 줄 전체를 쓴다(막대가 많아 반칸으로는 날짜가 뭉개짐) */}
                    <div className="rounded-xl border border-slate-200 shadow-sm p-4 lg:col-span-2">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <div className="text-sm font-bold text-slate-700">일별 판매 합계</div>
                        <div className="ml-auto"><RangeChips value={reportRange} onChange={setReportRange}/></div>
                      </div>
                      <div className="text-xs text-slate-500 mb-2">{reportRange===0?"전체 기간":rangeLabel} · 전 품목 일별 판매수량 합계</div>
                      {dailySalesTotals.length===0?<div className="py-16 text-center text-sm text-slate-400">일별 판매 기록이 있어야 표시됩니다</div>:
                      <ChartCanvas height={320} ariaLabel="일별 판매 합계 막대 차트"
                        type="bar"
                        data={{labels:rangeDates.map(d=>d.slice(5)),datasets:[{label:"일별 판매 합계",data:dailySalesTotals,backgroundColor:"#1D9E75",borderRadius:4}]}}
                        options={{responsive:true,maintainAspectRatio:false,plugins:{legend:{display:false},tooltip:{callbacks:{title:items=>items[0]?.label,label:ctx=>" "+Math.round(ctx.raw).toLocaleString("ko-KR")+"개"}}},scales:{x:{ticks:{autoSkip:true,maxTicksLimit:rangeDates.length>40?20:undefined,maxRotation:45,minRotation:45,font:{size:10}}},y:{beginAtZero:true,title:{display:true,text:"판매수량"},ticks:{callback:v=>v.toLocaleString("ko-KR")}}}}}/>}
                    </div>
                    <div className="rounded-xl border border-slate-200 shadow-sm p-4 lg:col-span-2">
                      <div className="text-sm font-bold text-slate-700 mb-1">발주 우선순위</div>
                      <div className="text-xs text-slate-500 mb-2">왼쪽 위일수록 발주 시급 (판매 많고 곧 소진)</div>
                      {scatterPts.length===0?<div className="py-16 text-center text-sm text-slate-400">판매·가용일수 데이터가 필요합니다</div>:
                      <ChartCanvas height={280} ariaLabel="가용일수 대비 월판매량 산점도, 발주 우선순위"
                        type="scatter"
                        data={{datasets:[{label:"상품",data:scatterPts,backgroundColor:"rgba(226,75,74,0.55)",borderColor:"#A32D2D"}]}}
                        options={{responsive:true,maintainAspectRatio:false,layout:{padding:10},plugins:{legend:{display:false},tooltip:{callbacks:{label:ctx=>{const p=ctx.raw;return `${p.name} (가용 ${p.x}일 · 월 ${p.y})`;}}}},scales:{x:{title:{display:true,text:"가용일수 (작을수록 급함) →"},min:0},y:{title:{display:true,text:"월판매량"},min:0}}}}/>}
                    </div>
                  </div>

                  {/* 재고자산 카테고리별 구성 + 판매 TOP/하위 */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="rounded-xl border border-slate-200 shadow-sm p-4">
                      <div className="text-sm font-bold text-slate-700 mb-2">재고자산 카테고리별 구성</div>
                      {categoryValue.length===0?<div className="py-16 text-center text-sm text-slate-400">단가가 입력된 품목이 있어야 표시됩니다</div>:
                      <ChartCanvas height={300} ariaLabel="재고자산 카테고리별 구성 도넛 차트"
                        type="doughnut"
                        data={{labels:categoryValue.map(c=>c[0]),datasets:[{data:categoryValue.map(c=>Math.round(c[1])),backgroundColor:["#185FA5","#1D9E75","#BA7517","#C026D3","#E24B4A","#7C3AED","#0D9488","#D85A30","#639922","#888780","#D4537E"],borderWidth:2,borderColor:"#fff"}]}}
                        options={{responsive:true,maintainAspectRatio:false,cutout:"55%",plugins:{legend:{display:true,position:"right",labels:{boxWidth:12,font:{size:11}}},tooltip:{callbacks:{label:ctx=>` ${ctx.label}: ₩${Math.round(ctx.raw).toLocaleString("ko-KR")}`}}}}}/>}
                    </div>
                    <div className="rounded-xl border border-slate-200 shadow-sm p-4">
                      <div className="text-sm font-bold text-slate-700 mb-2">판매 TOP 10 / 하위 10 ({rangeLabel})</div>
                      {salesRanked.length===0?<div className="py-16 text-center text-sm text-slate-400">일별 판매 기록이 있어야 표시됩니다</div>:
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-xs font-semibold text-emerald-700 mb-1">▲ 판매 상위</div>
                          <table className="w-full text-xs"><tbody>
                          {salesTop10.map((v,i)=>(<tr key={v.code} className="border-b border-slate-50"><td className="py-1 text-slate-400 w-5 align-top">{i+1}</td><td className="py-1 leading-snug break-words" title={`${v.code} · ${v.name}`}>{v.name}</td><td className="py-1 pl-1.5 text-right text-[10px] font-normal text-slate-500 tabular-nums whitespace-nowrap align-top">{nf(v.total)}</td></tr>))}
                          </tbody></table>
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-rose-600 mb-1">▼ 판매 하위</div>
                          <table className="w-full text-xs"><tbody>
                          {salesBottom10.map((v,i)=>(<tr key={v.code} className="border-b border-slate-50"><td className="py-1 text-slate-400 w-5 align-top">{i+1}</td><td className="py-1 leading-snug break-words" title={`${v.code} · ${v.name}`}>{v.name}</td><td className="py-1 pl-1.5 text-right text-[10px] font-normal text-slate-500 tabular-nums whitespace-nowrap align-top">{nf(v.total)}</td></tr>))}
                          </tbody></table>
                        </div>
                      </div>}
                    </div>
                  </div>

                  {/* ABC 분석 */}
                  <div className="rounded-xl border border-slate-200 shadow-sm p-4">
                    <div className="text-sm font-bold text-slate-700 mb-2">ABC 분석 (재고금액 기준)</div>
                    <div className="text-xs text-slate-500 mb-2">상위 상품들이 전체 재고금액의 대부분을 차지. 파레토 원칙(상위 20%가 80%)을 통해 집중관리 품목 파악</div>
                    {abcAnalysis.length===0?<div className="py-12 text-center text-sm text-slate-400">단가가 입력된 품목이 있어야 표시됩니다</div>:
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"16px",alignItems:"start"}}>
                      <ChartCanvas height={340} ariaLabel="ABC 분석 파레토 차트: 상품별 재고금액과 누적 비중"
                        type="bar"
                        data={{
                          labels:abcAnalysis.slice(0,20).map((a,i)=>a.name.length>12?a.name.slice(0,12)+"…":a.name),
                          datasets:[
                            {label:"재고금액(원)",data:abcAnalysis.slice(0,20).map(a=>a.value),backgroundColor:abcAnalysis.slice(0,20).map(a=>a.cumPct<=70?"#A32D2D":a.cumPct<=90?"#D97706":"#639922"),borderRadius:3,yAxisID:"y"},
                            {type:"line",label:"누적비중(%)",data:abcAnalysis.slice(0,20).map(a=>a.cumPct),borderColor:"#1A6B45",backgroundColor:"#1A6B45",tension:.2,pointRadius:3,yAxisID:"y1"}
                          ]
                        }}
                        options={{
                          responsive:true,maintainAspectRatio:false,
                          plugins:{legend:{display:false},tooltip:{callbacks:{label:ctx=>{const idx=ctx.datasetIndex;return idx===0?" ₩"+Math.round(ctx.raw).toLocaleString("ko-KR"):" "+ctx.raw+"%";}}}},
                          scales:{y:{position:"left",title:{display:true,text:"재고금액(원)"},ticks:{callback:v=>"₩"+Math.round(v).toLocaleString("ko-KR")}},y1:{position:"right",min:0,max:100,title:{display:true,text:"누적비중(%)"},grid:{drawOnChartArea:false}}}
                        }}/>
                      <div>
                        <div className="text-xs font-semibold text-slate-600 mb-2">등급별 현황</div>
                        <table className="w-full text-xs"><tbody>
                          <tr className="border-b border-slate-200"><td className="py-1.5 font-semibold">등급</td><td className="py-1.5 text-right">품목수</td><td className="py-1.5 text-right">누적금액</td></tr>
                          <tr className="border-b border-slate-100 bg-red-50"><td className="py-1.5">A등급 (상위 70%)</td><td className="py-1.5 text-right text-slate-500">{abcAnalysis.filter(a=>a.cumPct<=70).length}</td><td className="py-1.5 text-right font-semibold">₩{abcAnalysis.filter(a=>a.cumPct<=70).length>0?Math.round(abcAnalysis.filter(a=>a.cumPct<=70)[abcAnalysis.filter(a=>a.cumPct<=70).length-1].cumValue).toLocaleString("ko-KR"):"0"}</td></tr>
                          <tr className="border-b border-slate-100 bg-amber-50"><td className="py-1.5">B등급 (70~90%)</td><td className="py-1.5 text-right text-slate-500">{abcAnalysis.filter(a=>a.cumPct>70&&a.cumPct<=90).length}</td><td className="py-1.5 text-right font-semibold">₩{abcAnalysis.filter(a=>a.cumPct>70&&a.cumPct<=90).length>0?Math.round(abcAnalysis.filter(a=>a.cumPct>70&&a.cumPct<=90)[abcAnalysis.filter(a=>a.cumPct>70&&a.cumPct<=90).length-1].cumValue).toLocaleString("ko-KR"):"0"}</td></tr>
                          <tr className="bg-green-50"><td className="py-1.5">C등급 (90%~)</td><td className="py-1.5 text-right text-slate-500">{abcAnalysis.filter(a=>a.cumPct>90).length}</td><td className="py-1.5 text-right font-semibold">₩{abcAnalysis.length>0?Math.round(abcAnalysis[abcAnalysis.length-1].cumValue).toLocaleString("ko-KR"):"0"}</td></tr>
                        </tbody></table>
                      </div>
                    </div>}
                  </div>

                  {[
                    {title:`① 부족재고 — 가용일수 ${warnDays}일 미만 또는 재고 0 이하`,color:"red",rows:items.filter(it=>it.status==="부족").sort((a,b)=>(a.days??1e9)-(b.days??1e9)),cols:[{l:"상품명",f:it=>it.name},{l:"현재고",f:it=>nf(it.stock),r:1},{l:"월판매량",f:it=>nf(it.monthly),r:1},{l:"가용일수",f:it=>dc(it.days),r:1},{l:"발주량",f:it=>nf(it.order),r:1}]},
                    {title:`② 과잉재고 — 재고 ${overMonths}개월분 이상 또는 판매 없이 재고만`,color:"blue",rows:items.filter(it=>it.status==="과잉").sort((a,b)=>(b.months??1e9)-(a.months??1e9)),cols:[{l:"상품명",f:it=>it.name},{l:"현재고",f:it=>nf(it.stock),r:1},{l:"월판매량",f:it=>nf(it.monthly),r:1},{l:"재고개월수",f:it=>it.monthly>0?dc(it.months):"판매없음",r:1},{l:"재고금액",f:it=>won(it.value),r:1}]},
                    {title:"③ 변동성이 큰 재고 — CV% 상위 30",color:"amber",rows:volRows.filter(v=>v.cv!=null).sort((a,b)=>b.cv-a.cv).slice(0,30),empty:"일일재고기록 탭에 2일 이상 기록이 쌓이면 자동으로 채워집니다",cols:[{l:"상품명",f:v=>v.name},{l:"기록일수",f:v=>v.n,r:1},{l:"평균재고",f:v=>v.mean==null?"—":nf(Math.round(v.mean)),r:1},{l:"변동폭",f:v=>v.range==null?"—":nf(v.range),r:1},{l:"변동계수%",f:v=>dc(v.cv),r:1}]},
                    {title:"④ 판매 변동성이 큰 상품 — 판매 CV% 상위 30",color:"amber",rows:salesVolRows.filter(v=>v.cv!=null).sort((a,b)=>b.cv-a.cv).slice(0,30),empty:"일일판매기록 탭에 2일 이상 기록이 쌓이면 자동으로 채워집니다",cols:[{l:"상품명",f:v=>v.name},{l:"기록일수",f:v=>v.n,r:1},{l:"기간판매",f:v=>nf(v.total),r:1},{l:"일평균",f:v=>v.mean==null?"—":nf(Math.round(v.mean)),r:1},{l:"변동계수%",f:v=>dc(v.cv),r:1}]},
                    {title:"⑤ 판매 급증/급감 추세 상품",color:"blue",rows:salesVolRows.filter(v=>v.trend!=null&&v.trend!==0).sort((a,b)=>Math.abs(b.trend)-Math.abs(a.trend)).slice(0,30),empty:"일일판매기록이 쌓이면 첫날 대비 최근일 판매 변화를 보여줍니다",cols:[{l:"상품명",f:v=>v.name},{l:"첫날대비추세",f:v=>(v.trend>0?"▲ +":"▼ ")+nf(v.trend),r:1},{l:"최근일판매",f:v=>nf(v.recent),r:1},{l:"일평균",f:v=>v.mean==null?"—":nf(Math.round(v.mean)),r:1}]},
                  ].map(({title,color,rows,cols,empty})=>{
                    const hc=color==="red"?"bg-red-600":color==="blue"?"bg-blue-600":"bg-amber-500";
                    return(
                      <div key={title} className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
                        <div className={"px-4 py-2.5 text-sm font-bold text-white "+hc}>{title} · {rows.length}건</div>
                        {rows.length===0?<div className="py-8 text-center text-sm text-slate-400">{empty||"해당 품목 없음"}</div>:(
                          <div className="overflow-x-auto"><table className="w-full border-collapse text-sm">
                            <thead><tr className="bg-slate-50 border-b border-slate-200">{cols.map((c,i)=><th key={i} className={"px-3 py-2 text-xs font-semibold text-slate-500 "+(c.r?"text-right":"text-left")}>{c.l}</th>)}</tr></thead>
                            <tbody>{rows.map((it,ri)=><tr key={ri} className="border-b border-slate-100 hover:bg-slate-50">{cols.map((c,ci)=><td key={ci} className={"px-3 py-2.5 "+(c.r?"text-right tabular-nums text-slate-600":"font-medium text-slate-800")}>{c.f(it)}</td>)}</tr>)}</tbody>
                          </table></div>
                        )}
                      </div>
                    );
                  })}
                  </div>
                </div>
              )}
            </div>

            <p className={"mt-3 text-xs text-slate-400 text-center "+(isTableTab?"pb-8":"")}>월판매량 = 최근 30일 일별 판매 합계(기록 30일 미만 시 일평균×30 환산, 일별 기록 없으면 월별 판매 엑셀 사용) · 가용일수 = (현재고+발주) ÷ 1일판매량 · 재고개월수 = (현재고+발주) ÷ 월판매량 · 재고금액 = 재고량 × 입고단가(VAT 별도) · S코드(세트)·무재고·무판매 제외</p>
          </>
        )}

        {/* FILE FORMAT GUIDE (화면 하단) */}
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5 mt-6 shadow-sm">
          <div className="text-sm font-bold text-slate-700 mb-3">📋 파일 형식 안내</div>
          <div className="flex flex-wrap gap-2.5">
            {/* 일별 판매 기록이 있으면 월별 판매(sales) 업로드는 숨김 — 최근 30일 합계가 월판매량을 대체 */}
          {["stock",...(dailyMonthly?[]:["sales"]),"salesDaily"].map(type=>{
              const m=SLOT_META[type];
              return(
                <span key={type} className="inline-flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-xs font-medium text-slate-600">
                  <span className={"h-2 w-2 rounded-full "+m.dot}/>{TYPE_LABEL[type]}: <span className="text-slate-400">{m.fmt}</span>
                </span>
              );
            })}
            <span className="inline-flex items-center gap-2 rounded-lg bg-teal-50 border border-teal-100 px-3 py-2 text-xs font-medium text-teal-700">
              <span className="h-2 w-2 rounded-full bg-teal-500"/>구매계획: <span className="text-teal-500">발주 앱 이력에서 자동 반영 (업로드 불필요)</span>
            </span>
            <span className="inline-flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-xs font-medium text-amber-700">
              <span className="h-2 w-2 rounded-full bg-amber-500"/>단가: <span className="text-amber-600">단가마스터 앱에서 자동 반영 (업로드 불필요)</span>
            </span>
          </div>
        </div>
      </div>

      {/* SETTINGS MODAL */}
      {showSettings&&(
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={e=>e.target===e.currentTarget&&setShowSettings(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm">
            <h2 className="text-base font-bold text-slate-800 mb-4">설정</h2>
            <div className="space-y-4">
              <div><label className="block text-xs font-semibold text-slate-500 mb-1">부족 경고 기준 (가용일수 미만)</label><div className="flex items-center gap-2"><input type="number" defaultValue={warnDays} id="cfg-warn" className="w-24 rounded-lg border border-slate-200 px-3 py-2 text-right text-sm"/><span className="text-sm text-slate-500">일</span></div></div>
              <div><label className="block text-xs font-semibold text-slate-500 mb-1">과잉 기준 (재고개월수 이상)</label><div className="flex items-center gap-2"><input type="number" defaultValue={overMonths} id="cfg-over" className="w-24 rounded-lg border border-slate-200 px-3 py-2 text-right text-sm"/><span className="text-sm text-slate-500">개월</span></div></div>
              {isAdmin&&(
                <div>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={autoMonthEndSnap} onChange={e=>setAutoMonthEndSnap(e.target.checked)} className="mt-0.5"/>
                    <span>
                      <span className="block text-xs font-semibold text-slate-600">월말 자동 스냅샷</span>
                      <span className="block text-[11px] text-slate-400 mt-0.5">현재고 파일의 기준일이 그 달의 마지막 날이면 접속 시 자동으로 재고를 기록합니다. 매출원가 계산에 필요한 기말재고가 빠지지 않습니다.</span>
                    </span>
                  </label>
                </div>
              )}
            </div>
            <div className="mt-5 pt-4 border-t border-slate-100">
              <label className="block text-xs font-semibold text-slate-500 mb-2">클라우드 동기화 (팀 공유)</label>
              {user?(
                <div className="flex flex-wrap items-center gap-2">
                  {isAdmin&&<button onClick={syncToCloud} disabled={syncing} className="inline-flex items-center gap-1.5 rounded bg-[#0a6ed1] px-3 py-2 text-sm font-medium text-white hover:bg-[#0854a0] disabled:opacity-50"><Cloud className="h-4 w-4"/>클라우드 저장</button>}
                  <button onClick={loadFromCloud} disabled={syncing} className="inline-flex items-center gap-1.5 rounded border border-[#d9d9d9] bg-white px-3 py-2 text-sm font-medium text-[#32363a] hover:bg-[#f2f2f2] disabled:opacity-40"><CloudOff className="h-4 w-4"/>클라우드 불러오기</button>
                  {!isAdmin&&<span className="text-xs text-slate-400">읽기 전용 — 저장은 관리자만 가능</span>}
                  <span className="text-xs text-slate-400 ml-auto">{user.displayName||user.email||"익명 사용자"}</span>
                </div>
              ):(
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={handleGoogleLogin} className="inline-flex items-center gap-1.5 rounded bg-[#0a6ed1] px-3 py-2 text-sm font-medium text-white hover:bg-[#0854a0]"><LogIn className="h-4 w-4"/>구글 로그인</button>
                  <button onClick={handleAnonLogin} className="inline-flex items-center gap-1.5 rounded border border-[#d9d9d9] px-3 py-2 text-sm font-medium text-slate-500 hover:bg-[#f2f2f2]"><LogIn className="h-4 w-4"/>익명으로 시작</button>
                </div>
              )}
            </div>
            {isAdmin&&(
              <div className="mt-5 pt-4 border-t border-slate-100">
                <label className="block text-xs font-semibold text-slate-500 mb-2">주간 경영관리 리포트 발송 (매주 월요일 08:00)</label>
                <div className="space-y-2">
                  <input type="text" placeholder="수신자 이메일 (쉼표로 구분)" value={reportCfg.recipients}
                    onChange={e=>setReportCfg(c=>({...c,recipients:e.target.value}))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"/>
                  <input type="email" placeholder="발송용 Gmail 주소" value={reportCfg.gmailUser}
                    onChange={e=>setReportCfg(c=>({...c,gmailUser:e.target.value}))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"/>
                  <input type="password" placeholder="Gmail 앱 비밀번호 (16자리)" value={reportCfg.gmailAppPassword}
                    onChange={e=>setReportCfg(c=>({...c,gmailAppPassword:e.target.value}))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"/>
                  <label className="flex items-center gap-2 text-xs text-slate-500">
                    <input type="checkbox" checked={reportCfg.enabled} onChange={e=>setReportCfg(c=>({...c,enabled:e.target.checked}))}/>
                    매주 월요일 자동 발송 사용
                  </label>
                  <p className="text-[11px] text-slate-400">Gmail 일반 비밀번호가 아니라 구글 계정의 "앱 비밀번호"(16자리)를 발급받아 입력해야 합니다.</p>
                  <div className="flex gap-2 pt-1">
                    <button onClick={saveReportSettings} disabled={reportSaving} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded bg-[#0a6ed1] px-3 py-2 text-sm font-medium text-white hover:bg-[#0854a0] disabled:opacity-50">{reportSaving?"저장 중…":"설정 저장"}</button>
                    <button onClick={testSendReport} disabled={testSending} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded border border-[#d9d9d9] px-3 py-2 text-sm font-medium text-[#32363a] hover:bg-[#f2f2f2] disabled:opacity-50">{testSending?"발송 중…":"지금 테스트 발송"}</button>
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-between items-center mt-6">
              <button onClick={clearAll} className="inline-flex items-center gap-1.5 rounded border border-[#bb0000]/40 px-3 py-2 text-sm font-medium text-[#bb0000] hover:bg-[#fde8e8]"><Trash2 className="h-4 w-4"/>데이터 비우기</button>
              <div className="flex gap-2">
                <button onClick={()=>setShowSettings(false)} className={Ghost}>취소</button>
                <button onClick={()=>{setWarnDays(Number(document.getElementById("cfg-warn").value)||30);setOverMonths(Number(document.getElementById("cfg-over").value)||6);persist({warnDays,overMonths});setShowSettings(false);}} className={G}>저장</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TOAST */}
      {msg&&(
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className={"rounded-xl px-5 py-3 text-sm font-medium text-white shadow-xl "+(msg.kind==="error"?"bg-red-600":msg.kind==="success"?"bg-emerald-600":"bg-slate-800")}>{msg.text}</div>
        </div>
      )}
    </div>
  );
}
function parseName(name){const m=String(name).match(/(\d{4})(\d{2})(\d{2})(\d{2})?(\d{2})?/);if(!m)return"";let s=`${m[1]}-${m[2]}-${m[3]}`;if(m[4])s+=` ${m[4]}:${m[5]||"00"}`;return s;}
